// ─────────────────────────────────────────────────────────────────────────────
// PATIENT APP build of FormRenderer (Form Builder intake sheets).
//
// Derived from the doctor-side FormRenderer.js (2026-10-13). Differences:
//   • SAVE-ONLY: no PDF/print/narrative code (html2pdf, page-break avoidance,
//     print-filled-only, narrative sentences, attachment merge, live-mirror pane,
//     Preview PDF / Sample Data buttons). Nothing here ever generates a PDF.
//   • Staff-only pieces removed: Referral Attachments (renders nothing), signing
//     popup helpers, session lookups the patient app does not have.
//   • Patient behaviour kept: `disabledPrefilled` (auto-filled patient data and the
//     provider box are locked), `suppressedFieldIds` (hideInPatientApp), 16px
//     inputs (no iOS zoom-on-focus), `min(390px,100%)` mobile width.
//   • `valueOverrides._force` token re-applies every override once (used when the
//     same copied signature must be pushed onto a tab again).
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import BodyMapField from './BodyMapField';

const BRAND = '#0aa375';
const BRAND_LIGHT = '#e6f7f2';

const SIG_LABELS = {
  sig_patient:'Signature of Patient', sig_provider:'Signature of Provider',
  sig_doctor:'Signature of Doctor',   sig_attorney:'Signature of Attorney',
  sig_supervisor:'Supervising Physician Signature',
  sig_custom:'Custom Signature',
};

const isSig   = t => ['sig_patient','sig_provider','sig_doctor','sig_attorney','sig_supervisor','sig_custom'].includes(t);

// ── Doctor Signature label override ─────────────────────────────────────────
// For the `sig_doctor` field only, the printed/on-screen label must reflect
// WHO IS ACTUALLY SIGNING today — the SignatureTitle configured against the
// currently-selected Physician in session — rather than a static label the
// form author typed in at design time (that label field is still shown and
// editable in the Properties Panel, but is only used as the fallback below
// when no physician/title can be resolved, e.g. previewing outside a real
// session or a physician with no SignatureTitle set).
const getDoctorTitleSignature = (fallback) => {
  try {
    const { Physician, AssociatedDoctorList } = {}; // patient app has no staff session
    if (!Physician || !AssociatedDoctorList) return fallback;
    const doctor = AssociatedDoctorList.find(item => item.DoctorId === Physician.ID);
    if (!doctor || !doctor.SignatureTitle) return fallback;
    const title = doctor.SignatureTitle.trim();
    return title ? title : fallback;
  } catch {
    return fallback;
  }
};
const isLayout= t => ['heading','paragraph','label','divider','page_break','section_start','section_end','tab_break'].includes(t);
const hasOpts = t => ['dropdown','dropdown_searchable','radio','checkbox'].includes(t);

function isFieldEmptyForPrint(field, value) {
  const t = field.type;
  if (t === 'checkbox' || t === 'multiselect_dropdown' || t === 'file_upload') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (t === 'single_checkbox') return !value;
  if (t === 'textarea') {
    // Rich Text Area stores HTML — strip tags/entities before judging emptiness
    // so "<p><br></p>" (an untouched editor) counts as blank, not "filled".
    if (value === undefined || value === null) return true;
    return String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
  }
  if (t === 'inline_row') {
    const segs = field.segments || [];
    // A text_label segment is static instructional/legal text baked into the
    // row (e.g. "I, ___, hereby assign to ___ ("Assignee") all rights...")
    // — not a fill-in answer, but unlike every other segment type its
    // CONTENT must never be swept away just because its sibling blank
    // fill-in segments have nothing in them yet. Bug found 2026-10-01: a
    // legal-form inline_row with real caption text next to untouched
    // blank-line fields was being treated as "fully empty" here (every
    // check below ignores text_label and found no filled segment), which
    // then had printFilledOnly hide the WHOLE row — erasing actual document
    // wording, not just blank answer space. So a row with any non-blank
    // text_label content is never eligible to be hidden at all, regardless
    // of whether its fill-in segments are empty.
    if (segs.some(s => s.segType === 'text_label' && String(s.text || '').trim() !== '')) return false;
    return segs.every(s => {
      if (s.segType === 'text_label') return true; // static label text, not an answer
      const segVal = (value || {})[s.id];
      if (s.fieldType === 'single_checkbox') return !segVal;
      if (Array.isArray(segVal)) return segVal.length === 0;
      return segVal === undefined || segVal === null || String(segVal).trim() === '';
    });
  }
  if (t === 'table') {
    // Dynamic/API-sourced rows (prefilled+prefillKey) aren't something a person "fills in" cell by cell —
    // never hide those, same reasoning as provider_dropdown/patient_info_strip.
    if (field.prefilled && field.prefillKey) return false;
    const cells = (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
    return Object.values(cells).every(cv =>
      Array.isArray(cv) ? cv.length === 0 : (cv === undefined || cv === null || String(cv).trim() === '')
    );
  }
  if (t === 'body_map') {
    // Best-effort generic check — BodyMapField.js's exact value shape isn't
    // available in this codebase copy, so this checks for "nothing at all
    // rather than assuming a specific shape: no value, an empty array of
    // marks, or an object whose every property is itself an empty array or
    // falsy (e.g. {front:[],back:[]}). If marks are stored some other way and
    // this still doesn't hide/show correctly, this needs a real look at
    // BodyMapField.js's value shape rather than another guess.
    if (!value) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.values(value).every(v => Array.isArray(v) ? v.length === 0 : !v);
    return false;
  }
  // text, number, date, dropdown, dropdown_searchable, radio, sig_* — plain
  // scalar emptiness check.
  return value === undefined || value === null || String(value).trim() === '';
}

// ── Width calculation (mirrors editor) ────────────────────────────────────────
function getW(field, mobile=false) {
  if (mobile) return '100%';
  if (!field.width||field.width==='full') return '100%';
  if (field.width==='custom'&&field.customWidthPct) return `${field.customWidthPct}%`;
  const map={half:'calc(50% - 6px)',third:'calc(33.333% - 8px)',quarter:'calc(25% - 9px)',sixth:'calc(16.667% - 10px)'};
  return map[field.width]||'100%';
}

// ── Build rows (mirrors editor) ───────────────────────────────────────────────
function buildRows(fields) {
  const rows=[]; let row=[], pct=0;
  fields.forEach(f=>{
    if(f.type==='inline_row'){if(row.length>0){rows.push(row);row=[];pct=0;}rows.push([f]);return;}
    const w=f.width==='custom'&&f.customWidthPct?parseFloat(f.customWidthPct):{full:100,half:50,third:33.333,quarter:25,sixth:16.667}[f.width]||100;
    if(pct+w>100.5){if(row.length>0)rows.push(row);row=[f];pct=w;}
    else{row.push(f);pct+=w;}
  });
  if(row.length>0)rows.push(row);
  return rows;
}

// ── Group rows into visual "sections" for the rounded-corner card styling —
// a new section starts ONLY at a row containing an explicit `section_start`
// marker field (never inferred from a `heading` field — heading is just a
// plain title elsewhere on the form, and grouping fields by "next heading"
// guessed wrong which fields actually belonged together). The user drags a
// `section_start` field to exactly where they want a card to begin; it runs
// until the next such marker (or the end of the form). Rows before the
// first marker (or in a form with no markers at all) stay in an ungrouped
// "lead" section that never gets card styling, so a form with no markers
// renders exactly as it always has. A marker can opt out of the card via
// `cardWrap:false` (default true) for cases where grouping doesn't make
// sense — e.g. only wanting the logical grouping (for collapsible/tabs
// later) without the visual border. Only used for the live fill view (see
// .frm-section-card's print override above, which strips the visual chrome
// for PDF capture).
function buildSections(rows) {
  const sections = [{ key:'__lead__', cardWrap:false, hasStart:false, rows:[] }];
  rows.forEach(row=>{
    const startField = row.find(f=>f.type==='section_start');
    const endField = row.find(f=>f.type==='section_end');
    if (startField) {
      sections.push({
        key: startField.id,
        cardWrap: startField.cardWrap !== false,
        hasStart: true,
        // Collapsible only makes sense with a visible title to click on —
        // the Properties Panel already hides the toggle when there's no
        // title, but guard here too in case a title gets cleared later.
        collapsible: !!startField.collapsible && !!(startField.label && startField.label.trim()),
        defaultCollapsed: !!startField.defaultCollapsed,
        rows: [row],
      });
    } else {
      sections[sections.length-1].rows.push(row);
      // A `section_end` marker closes the section it's inside — its own row
      // stays in that section (so it sits inside the closing card, though it
      // renders no visible content), and everything after it drops back to
      // an ungrouped "gap" section (no card) until the next Section Start
      // reopens grouping. Mirrors the `__lead__` section's cardWrap:false.
      if (endField) sections.push({ key:`__gap_${endField.id}`, cardWrap:false, hasStart:false, rows:[] });
    }
  });
  return sections.filter(s=>s.rows.length);
}
const SECTION_CARD_STYLE = { background:'#fff', border:'1px solid #e9ecef', borderRadius:'10px', padding:'14px 16px 16px', marginBottom:'14px', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' };

// ── Group rows into top-level "tabs" — an orthogonal, higher-level grouping
// than buildSections: tabs partition the WHOLE form into pages navigated via
// a tab bar, while buildSections still groups rows into cards WITHIN each
// tab's own content. A `tab_break` field (a pure marker, like page_break/
// section_start/section_end — never rendered inline) starts a new tab;
// everything before the first one forms an implicit first tab. If the form
// has no `tab_break` fields at all, this returns a single tab whose rows are
// the entire form — the caller uses that to skip rendering any tab bar UI,
// so a form with no tabs looks and behaves exactly as it always did.
// IMPORTANT (mirrors the collapsible-section PDF rule): tabs are an
// on-screen navigation convenience only. Every tab's fields must always be
// in the DOM and forced visible for PDF/print — never conditionally
// unmounted — so switching which tab is "active" on screen never removes
// content from the saved PDF record.
function buildTabs(rows) {
  const tabs = [{ key:'__tab_lead__', label:null, rows:[] }];
  rows.forEach(row=>{
    const tabField = row.find(f=>f.type==='tab_break');
    if (tabField) {
      tabs.push({ key:tabField.id, label: (tabField.label && tabField.label.trim()) || `Tab ${tabs.length+1}`, rows:[] });
    } else {
      tabs[tabs.length-1].rows.push(row);
    }
  });
  const nonEmpty = tabs.filter(t=>t.rows.length);
  // The lead tab (before the first tab_break) has no marker field of its own
  // to carry a title — default it to "General" only when it's actually kept
  // (i.e. there's real content before the first tab_break); otherwise it's
  // filtered out above like any other empty tab, so a tab_break placed right
  // at the top of the form doesn't produce an empty leading tab.
  return nonEmpty.map(t => t.key==='__tab_lead__' ? { ...t, label:'General' } : t);
}

// ── Signature Pad ─────────────────────────────────────────────────────────────
function SignaturePad({fieldId, value, onCapture, height=160}) {
  const cr=useRef(null), dr=useRef(false);

  // Restore saved signature onto canvas on mount (and whenever value changes from outside)
  useEffect(()=>{
    if(!cr.current) return;
    if(!value) {
      // Value cleared — wipe the canvas
      cr.current.getContext('2d').clearRect(0,0,cr.current.width,cr.current.height);
      return;
    }
    const img=new Image();
    img.onload=()=>{
      if(!cr.current) return; // canvas unmounted while the image was loading (e.g. signing popup closed)
      const ctx=cr.current.getContext('2d');
      ctx.clearRect(0,0,cr.current.width,cr.current.height);
      ctx.drawImage(img,0,0,cr.current.width,cr.current.height);
    };
    img.src=value;
  },[value]);

  const gp=(e,r)=>{
    const scaleX=cr.current.width/r.width;
    const scaleY=cr.current.height/r.height;
    const clientX=e.touches?e.touches[0].clientX:e.clientX;
    const clientY=e.touches?e.touches[0].clientY:e.clientY;
    return {x:(clientX-r.left)*scaleX, y:(clientY-r.top)*scaleY};
  };
  const start=e=>{e.preventDefault();dr.current=true;const ctx=cr.current.getContext('2d'),{x,y}=gp(e,cr.current.getBoundingClientRect());ctx.beginPath();ctx.moveTo(x,y);ctx.strokeStyle='#212529';ctx.lineWidth=1.5;ctx.lineCap='round';};
  const draw=e=>{e.preventDefault();if(!dr.current)return;const ctx=cr.current.getContext('2d'),{x,y}=gp(e,cr.current.getBoundingClientRect());ctx.lineTo(x,y);ctx.stroke();};
  const stop=()=>{
    dr.current=false;
    if(!onCapture) return;
    // Check if canvas has any drawn pixels — compare against a blank canvas
    const canvas = cr.current;
    const ctx = canvas.getContext('2d');
    const blank = document.createElement('canvas');
    blank.width = canvas.width; blank.height = canvas.height;
    const isBlank = canvas.toDataURL() === blank.toDataURL();
    onCapture(isBlank ? '' : canvas.toDataURL());
  };
  const clear=()=>{const c=cr.current;c.getContext('2d').clearRect(0,0,c.width,c.height);if(onCapture)onCapture('');};
  return(
    <div style={{position:'relative'}}>
      <canvas ref={cr} width={600} height={160} className="sig-canvas"
        style={{border:'1px solid #dee2e6',borderRadius:'5px',background:'#fff',width:'100%',height:`${height}px`,cursor:'crosshair',touchAction:'none',display:'block'}}
        onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}/>
      <button className="sig-clear-btn" onClick={clear} style={{position:'absolute',top:'4px',right:'4px',fontSize:'10px',padding:'2px 7px',background:'rgba(255,255,255,.9)',border:'1px solid #dee2e6',borderRadius:'3px',cursor:'pointer',color:'#6c757d'}}>Clear</button>
    </div>
  );
}

// A drawn signature's value is always a canvas data-URL (see SignaturePad's
// stop() above — canvas.toDataURL() always starts with 'data:image'); a
// typed e-signature's value is just the plain name string the signer typed.
// This lets both modes share the one plain-string value slot on sig_* fields
// with no schema/shape migration — existing saved drawn signatures are
// unaffected, and which UI to show for a given value is always unambiguous.
const isDrawnSigValue = v => typeof v === 'string' && v.startsWith('data:image');

// ── Signature popup (patient app) ───────────────────────────────────────────────────────
// Same flow as the legacy intake sheets (AssignmentOfBenefit → SignaturePad): the signature
// box in the form is a read-only preview; tapping it opens a modal with a big canvas,
// Clear / Cancel / Save Signature (Save stays disabled until something is drawn), and the
// existing signature is pre-loaded so it can be redrawn or cleared. Uses the legacy sp-* markup and
// the legacy SignaturePad.css verbatim (injected below, so the patient app does not need to load it
// separately; if it already does, the rules are identical).
const downscaleSignature = (dataUrl, maxW = 700) => new Promise(resolve => {
  try {
    const img = new Image();
    img.onload = () => {
      if (!img.width || img.width <= maxW) return resolve(dataUrl);
      const r = maxW / img.width;
      const c = document.createElement('canvas');
      c.width = maxW; c.height = Math.max(1, Math.round(img.height * r));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  } catch (_) { resolve(dataUrl); }
});

const SIG_POPUP_CSS = `
/* ═══════════════════════════════════════════
   SignaturePad.css  —  Responsive Signature Modal
   ═══════════════════════════════════════════ */

/* ── Overlay ── */
.sp-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.sp-overlay--in  { animation: spFadeIn  0.22s ease forwards; }
.sp-overlay--out { animation: spFadeOut 0.18s ease forwards; }

@keyframes spFadeIn  { from { opacity: 0; } to { opacity: 1; } }
@keyframes spFadeOut { from { opacity: 1; } to { opacity: 0; } }

/* ── Modal ── */
.sp-modal {
  background: #ffffff;
  border-radius: 14px;
  width: 100%;
  max-width: 540px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.22),
    0 0 0 1px rgba(0, 128, 128, 0.12);
  overflow: hidden;
}

.sp-modal--in  { animation: spSlideIn  0.24s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
.sp-modal--out { animation: spSlideOut 0.16s ease forwards; }

@keyframes spSlideIn {
  from { transform: translateY(20px) scale(0.97); opacity: 0; }
  to   { transform: none; opacity: 1; }
}
@keyframes spSlideOut {
  from { transform: none; opacity: 1; }
  to   { transform: translateY(10px) scale(0.98); opacity: 0; }
}

/* ── Header ── */
.sp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 16px;
  background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
  color: #ffffff;
}

.sp-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sp-pen-icon {
  font-size: 26px;
  opacity: 0.9;
  line-height: 1;
}

.sp-header-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #ffffff;
}

.sp-header-sub {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
  color: #ffffff;
}

.sp-close-btn {
  background: rgba(255, 255, 255, 0.18);
  border: none;
  color: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
}

.sp-close-btn:hover {
  background: rgba(255, 255, 255, 0.32);
}

/* ── Canvas Wrapper ── */
.sp-canvas-wrapper {
  position: relative;
  margin: 20px 20px 0;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fffe;
  overflow: hidden;
  cursor: crosshair;
}

.sp-canvas {
  display: block;
  width: 100%;
  height: 190px;
  touch-action: none;
}

.sp-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 14px;
  pointer-events: none;
  user-select: none;
}

.sp-placeholder-icon {
  font-size: 32px;
  opacity: 0.35;
}

.sp-baseline {
  position: absolute;
  bottom: 36px;
  left: 20px;
  right: 20px;
  height: 1px;
  background: repeating-linear-gradient(
    90deg,
    #cbd5e1 0,
    #cbd5e1 6px,
    transparent 6px,
    transparent 14px
  );
  pointer-events: none;
}

/* ── Footer ── */
.sp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 20px;
  gap: 10px;
}

.sp-footer-right {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* ── Buttons ── */
.sp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.sp-btn--ghost {
  background: transparent;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
}

.sp-btn--ghost:hover {
  background: #f1f5f9;
  color: #334155;
}

.sp-btn--cancel {
  background: #f1f5f9;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.sp-btn--cancel:hover {
  background: #e2e8f0;
}

.sp-btn--save {
  background: linear-gradient(135deg, #0d9488, #0f766e);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(13, 148, 136, 0.35);
}

.sp-btn--save:hover:not(.sp-btn--disabled) {
  background: linear-gradient(135deg, #0f766e, #115e59);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(13, 148, 136, 0.4);
}

.sp-btn--disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* ══════════════════════════════════════════
   RESPONSIVE BREAKPOINTS
══════════════════════════════════════════ */

/* ── Tablet (≤ 600px) ── */
@media (max-width: 600px) {
  .sp-overlay {
    padding: 12px;
    align-items: flex-end;         /* sheet slides up from bottom on mobile */
  }

  .sp-modal {
    max-width: 100%;
    border-radius: 18px 18px 0 0;  /* bottom-sheet style */
  }

  .sp-modal--in {
    animation: spSheetIn 0.28s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
  }
  .sp-modal--out {
    animation: spSheetOut 0.2s ease forwards;
  }

  @keyframes spSheetIn {
    from { transform: translateY(100%); opacity: 0.6; }
    to   { transform: none; opacity: 1; }
  }
  @keyframes spSheetOut {
    from { transform: none; opacity: 1; }
    to   { transform: translateY(100%); opacity: 0; }
  }

  .sp-header {
    padding: 14px 16px 12px;
  }

  .sp-pen-icon {
    font-size: 22px;
  }

  .sp-header-title {
    font-size: 15px;
  }

  .sp-canvas-wrapper {
    margin: 16px 14px 0;
  }

  .sp-canvas {
    height: 160px;
  }

  .sp-footer {
    padding: 12px 14px 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .sp-footer-right {
    justify-content: stretch;
    gap: 8px;
  }

  .sp-btn--cancel,
  .sp-btn--save {
    flex: 1;
    justify-content: center;
  }

  .sp-btn--ghost {
    align-self: center;
  }
}

/* ── Small phones (≤ 400px) ── */
@media (max-width: 400px) {
  .sp-header-title { font-size: 14px; }
  .sp-header-sub   { font-size: 11px; }

  .sp-canvas { height: 140px; }

  .sp-btn {
    font-size: 13px;
    padding: 8px 14px;
  }

  .sp-baseline {
    bottom: 28px;
  }
}

/* ── Large screens (≥ 768px) ── */
@media (min-width: 768px) {
  .sp-modal {
    max-width: 560px;
  }

  .sp-canvas {
    height: 200px;
  }
}

/* ── Extra large (≥ 1200px) ── */
@media (min-width: 1200px) {
  .sp-modal {
    max-width: 600px;
  }

  .sp-canvas {
    height: 210px;
  }
}
`;

function SignaturePopup({ title, existingSign, onClose, onSave, onClear }) {
  const cvRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  // Size the canvas for the device pixel ratio once, then pre-load any existing signature.
  useEffect(() => {
    const c = cvRef.current;
    if (!c) return undefined;
    const dpr = window.devicePixelRatio || 1;
    const r = c.getBoundingClientRect();
    c.width = Math.max(1, Math.round(r.width * dpr));
    c.height = Math.max(1, Math.round(r.height * dpr));
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    let cancelled = false;
    if (isDrawnSigValue(existingSign)) {
      const img = new Image();
      img.onload = () => {
        if (cancelled || !cvRef.current) return;
        const k = Math.min(r.width / img.width, r.height / img.height);
        const w = img.width * k, h = img.height * k;
        ctx.drawImage(img, (r.width - w) / 2, (r.height - h) / 2, w, h); // keep aspect ratio
        setIsEmpty(false);
      };
      img.src = existingSign;
    }
    // lock page scroll behind the modal, close on Escape
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { cancelled = true; document.body.style.overflow = prevOverflow; document.removeEventListener('keydown', onKey); };
  }, []); // eslint-disable-line

  const xy = e => { const r = cvRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const down = e => {
    e.preventDefault();
    try { cvRef.current.setPointerCapture(e.pointerId); } catch (_) {}
    drawing.current = true;
    last.current = xy(e);
    // a single tap leaves a dot
    const ctx = cvRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(last.current.x + 0.01, last.current.y + 0.01); ctx.stroke();
    setIsEmpty(false);
  };
  const move = e => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = cvRef.current.getContext('2d');
    const p = xy(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const up = () => { drawing.current = false; };

  const clear = () => {
    const c = cvRef.current;
    if (!c) return;
    c.getContext('2d').save();
    c.getContext('2d').setTransform(1, 0, 0, 1, 0, 0);
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    c.getContext('2d').restore();
    setIsEmpty(true);
    onClear && onClear();
  };
  const save = async () => {
    const c = cvRef.current;
    if (!c || isEmpty) return;
    onSave(await downscaleSignature(c.toDataURL('image/png')));
  };

  return createPortal(
    <div className="sp-overlay sp-overlay--in" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{SIG_POPUP_CSS}</style>
      <div className="sp-modal sp-modal--in" role="dialog" aria-modal="true" aria-label={title || 'Signature'}>
        {/* header */}
        <div className="sp-header">
          <div className="sp-header-left">
            <span className="sp-pen-icon">✍</span>
            <div>
              <div className="sp-header-title">{title || 'Signature'}</div>
              <div className="sp-header-sub">Draw your signature below</div>
            </div>
          </div>
          <button type="button" className="sp-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {/* canvas area */}
        <div className="sp-canvas-wrapper">
          <canvas ref={cvRef} className="sp-canvas"
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}/>
          {isEmpty && (
            <div className="sp-placeholder">
              <span className="sp-placeholder-icon">✍</span>
              <span>Sign here</span>
            </div>
          )}
          <div className="sp-baseline"/>
        </div>
        {/* footer */}
        <div className="sp-footer">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={clear}><span>↺</span> Clear</button>
          <div className="sp-footer-right">
            <button type="button" className="sp-btn sp-btn--cancel" onClick={onClose}>Cancel</button>
            <button type="button" className={`sp-btn sp-btn--save${isEmpty ? ' sp-btn--disabled' : ''}`} onClick={save} disabled={isEmpty}><span>✓</span> Save Signature</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SignatureBox({ value, onChange, title, readOnly = false, height = 160 }) {
  const [open, setOpen] = useState(false);
  const cvRef = useRef(null);
  const drawn = isDrawnSigValue(value);
  useEffect(() => {
    const el = cvRef.current;
    if (!el) return undefined;
    const ctx = el.getContext('2d');
    ctx.clearRect(0, 0, el.width, el.height);
    if (!drawn) return undefined;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled || !cvRef.current) return;
      const r = Math.min(el.width / img.width, el.height / img.height);
      const w = img.width * r, h = img.height * r;
      ctx.clearRect(0, 0, el.width, el.height);
      ctx.drawImage(img, (el.width - w) / 2, (el.height - h) / 2, w, h); // keep aspect ratio — no stretching
    };
    img.src = value;
    return () => { cancelled = true; };
  }, [value, drawn]);
  return (
    <div style={{position:'relative'}}>
      <canvas ref={cvRef} width={600} height={160} className="sig-canvas"
        style={{border:'1px solid #dee2e6',borderRadius:'5px',background:'#fff',width:'100%',height:`${height}px`,display:'block'}}/>
      {!drawn && !readOnly && (
        <div className="sig-tap-hint" style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',gap:8,pointerEvents:'none',color:'#64748b',fontSize:14,fontWeight:500}}>
          <span aria-hidden="true" style={{fontSize:20}}>✍️</span>Tap here to sign
        </div>
      )}
      {!readOnly && <div role="button" tabIndex={0} aria-label={`Open signature pad${title ? ': ' + title : ''}`}
        onClick={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        style={{position:'absolute',inset:0,cursor:'pointer',borderRadius:'5px',background:'transparent'}}/>}
      {drawn && !readOnly && (
        <button type="button" className="sig-clear-btn" onClick={() => onChange('')} style={{position:'absolute',top:'4px',right:'4px',zIndex:2,fontSize:'12px',padding:'4px 10px',background:'rgba(255,255,255,.9)',border:'1px solid #dee2e6',borderRadius:'4px',cursor:'pointer',color:'#6c757d'}}>Clear</button>
      )}
      {open && <SignaturePopup title={title} existingSign={value}
        onClose={() => setOpen(false)}
        onClear={() => onChange('')}
        onSave={v => { onChange(v); setOpen(false); }}/>}
    </div>
  );
}


// ── sig_doctor physician-name resolution ────────────────────────────────────
// Different host screens populate the signing physician's name under
// different prefillData keys — the Referral Form modal (AssignedTemplateModal.js)
// uses referring_physician; MedicalNoteFormBuilder.js's own prefillData only
// ever sets doctor_name (no referring_physician key exists there at all).
// Tries the field's own explicit prefillKey first (so a deliberately-chosen
// Data Field always wins), then both known host keys, in that order. Returns
// undefined when nothing resolves, so the caller can tell "no name available
// yet" apart from "name really is an empty string".
function resolveSigDoctorName(f, prefillData) {
  const candidates = [f.prefillKey, 'referring_physician', 'doctor_name'].filter(Boolean);
  const key = candidates.find(k => {
    const v = prefillData[k];
    return v !== undefined && v !== null && v !== '' && typeof v !== 'object';
  });
  return key ? prefillData[key] : undefined;
}

// Patient name for a Type (e-signature) Patient Signature box (2026-10-08).
// Field's own explicit prefillKey first, then the keys the intake/referral
// hosts populate with the patient's name.
function resolvePatientSigName(f, prefillData) {
  const candidates = [f.prefillKey, 'patient_name', 'txtPatientFName'].filter(Boolean);
  const key = candidates.find(k => {
    const v = prefillData[k];
    return v !== undefined && v !== null && v !== '' && typeof v !== 'object';
  });
  return key ? prefillData[key] : undefined;
}

// ── Typed ("electronic") signature ──────────────────────────────────────────
// Additive alternative to SignaturePad, added 2026-09-30 per feedback that a
// hand-drawn signature isn't a true electronic signature for private-practice
// purposes — the reference sample note shows a typed name rendered in a
// cursive/script font next to "Electronically reviewed and signed by:"
// instead of a scanned/drawn signature. This does NOT replace SignaturePad —
// both remain available on every sig_* field via the Draw/Type toggle below;
// this only covers the "Type" side of that toggle.
//
// Font updated 2026-10-06: the original fallback stack (Brush Script MT /
// Segoe Script / Lucida Handwriting / Apple Chancery) are OS-installed fonts
// only, inconsistent across platforms and often thin/hard to read, and none
// of them render a true bold weight — reported as not "clear... stylish and
// bold" enough for a physician's typed name. Dancing Script is a proper
// variable-weight Google Font with a real 700 weight, so it's loaded (once,
// lazily, same pattern as loadHtml2Pdf below) and used first; the old OS
// fonts stay as a fallback stack for the brief moment before it loads or if
// the stylesheet request is ever blocked.
const SIG_SCRIPT_FONT = "'Dancing Script','Brush Script MT','Segoe Script','Lucida Handwriting','Apple Chancery',cursive";
let sigFontLoadStarted = false;
function loadSignatureFont() {
  if (sigFontLoadStarted || typeof document === 'undefined') return;
  sigFontLoadStarted = true;
  if (document.getElementById('frm-sig-font')) return;
  const link = document.createElement('link');
  link.id = 'frm-sig-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap';
  document.head.appendChild(link);
}
function TypedSignature({ value, onCapture, height = 160 }) {
  useEffect(() => { loadSignatureFont(); }, []);
  const clear = () => { if (onCapture) onCapture(''); };
  // Shrink the script font until the whole name fits the box (2026-10-08):
  // a fixed 36px clipped long names mid-word ("JANELLE SOLC") on narrow boxes.
  const inputRef = useRef(null);
  const [fontPx, setFontPx] = useState(36);
  useEffect(() => {
    const fit = () => {
      try {
        const el = inputRef.current;
        if (!el) return;
        const avail = el.clientWidth - 58 - 6; // minus left/right padding + slack
        if (avail <= 0) return;
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.font = `700 36px ${SIG_SCRIPT_FONT}`;
        const w = ctx.measureText(value || '').width;
        setFontPx(w > avail ? Math.max(14, Math.floor(36 * avail / w)) : 36);
      } catch (_) { /* measuring is best-effort */ }
    };
    fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [value]);
  return (
    <div style={{ position:'relative' }}>
      <input
        ref={inputRef}
        type="text"
        className="sig-type-input"
        value={value || ''}
        onChange={e => onCapture && onCapture(e.target.value)}
        placeholder="Type your full name"
        style={{
          width:'100%', height:`${height}px`, boxSizing:'border-box',
          border:'1px solid #dee2e6', borderRadius:'5px', background:'#fff',
          padding:'0 40px 0 18px', fontSize:`${fontPx}px`, fontWeight:700, color:'#1b3a34',
          fontFamily: SIG_SCRIPT_FONT, letterSpacing:'0.3px',
        }}
      />
      <button className="sig-clear-btn" onClick={clear} style={{position:'absolute',top:'4px',right:'4px',fontSize:'10px',padding:'2px 7px',background:'rgba(255,255,255,.9)',border:'1px solid #dee2e6',borderRadius:'3px',cursor:'pointer',color:'#6c757d'}}>Clear</button>
    </div>
  );
}

// ── File Upload — images and/or PDFs, attached to the generated PDF at the
// end (see generatePdfHtml's merge step below). Needs its own hooks (drag
// state, processing state), so it's a separate component rather than inline
// logic in the shared FieldRenderer branch — same reasoning as SignaturePad. ──
function FileUploadField({ field, value, onChange, readOnly }) {
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const inputRef = useRef(null);
  const files = Array.isArray(value) ? value : [];

  const acceptAttr = field.accept==='images' ? 'image/*' : field.accept==='pdf' ? 'application/pdf' : 'image/*,application/pdf';
  const maxBytes = (field.maxSizeMB || 8) * 1024 * 1024;

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });

  // Normalizes any image format to JPEG via canvas — pdf-lib can only embed
  // PNG/JPEG directly, so this guarantees every stored image is embeddable
  // later regardless of source format, and compresses large phone photos
  // along the way. If the browser can't decode the file (e.g. HEIC on some
  // browsers), this throws and the caller shows the user a clear message.
  const normalizeImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      img.onerror = () => reject(new Error('Could not read this image — try a JPG or PNG instead.'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });

  const handleFiles = async (fileList) => {
    if (readOnly) return;
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setProcessing(true);
    const accepted = [];
    for (const file of incoming) {
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      if (field.accept==='images' && !isImage) continue;
      if (field.accept==='pdf' && !isPdf) continue;
      if (!isImage && !isPdf) { alert(`${file.name}: only images or PDF files are accepted.`); continue; }
      if (file.size > maxBytes) { alert(`${file.name} is larger than ${field.maxSizeMB||8}MB — skipped.`); continue; }
      try {
        const alreadyJpegOrPng = file.type==='image/jpeg' || file.type==='image/png';
        const dataUrl = isImage && !alreadyJpegOrPng ? await normalizeImage(file) : await readAsDataUrl(file);
        accepted.push({
          id: `fu_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          name: file.name,
          type: isPdf ? 'application/pdf' : (file.type==='image/png' ? 'image/png' : 'image/jpeg'),
          dataUrl, size: file.size,
        });
      } catch (e) {
        alert(`${file.name}: ${e.message}`);
      }
    }
    setProcessing(false);
    if (!accepted.length) return;
    onChange(field.multiple===false ? [accepted[0]] : [...files, ...accepted]);
  };

  const removeFile = (id) => onChange(files.filter(f => f.id !== id));
  const acceptDesc = field.accept==='images' ? 'images' : field.accept==='pdf' ? 'PDF' : 'images or PDF';

  return (
    <div className="fu-widget-root">
      <div
        className="fu-dropzone"
        onClick={() => !readOnly && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); if (!readOnly) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); if (!readOnly) handleFiles(e.dataTransfer.files); }}
        style={{
          border: `2px dashed ${dragOver ? BRAND : '#dee2e6'}`, borderRadius: '8px', padding: '18px',
          textAlign: 'center', cursor: readOnly ? 'default' : 'pointer',
          background: dragOver ? BRAND_LIGHT : '#fafafa', transition: 'all .15s',
        }}
      >
        <input ref={inputRef} type="file" accept={acceptAttr} multiple={field.multiple!==false} disabled={readOnly}
          style={{ display: 'none' }} onChange={e => { handleFiles(e.target.files); e.target.value=''; }}/>
        <div style={{ fontSize: '22px' }}>📎</div>
        <div style={{ fontSize: '12px', color: '#495057', marginTop: '4px' }}>
          {processing ? 'Processing…' : readOnly ? (files.length ? '' : 'No files') :
            <>Click or drag files here <span style={{ color: '#adb5bd' }}>({acceptDesc})</span></>}
        </div>
      </div>

      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
          {files.map(f => (
            <div key={f.id} className="fu-file-item" data-fu-name={f.name} data-fu-type={f.type} data-fu-dataurl={f.dataUrl} style={{ position: 'relative', width: '84px' }}>
              {f.type.startsWith('image/') ? (
                <img src={f.dataUrl} alt={f.name} style={{ width: '84px', height: '84px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #dee2e6' }}/>
              ) : (
                <div style={{ width: '84px', height: '84px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #dee2e6', borderRadius: '6px', background: '#f8f9fa' }}>
                  <span style={{ fontSize: '26px' }}>📄</span>
                </div>
              )}
              <div className="fu-name" style={{ fontSize: '10px', color: '#6c757d', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.name}>{f.name}</div>
              {!readOnly && (
                <button className="fu-remove-btn" type="button" onClick={() => removeFile(f.id)} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', border: 'none', background: '#dc3545', color: '#fff', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Searchable dropdown ───────────────────────────────────────────────────────
// Patient-app variant: dropdown panel is portaled to <body> so it is never clipped by the
// intake sheet's scroll/overflow containers.
function SearchDropdown({options=[],placeholder='',value,onChange,multi=false,align='left',bold=false}) {
  const [open,setOpen]=useState(false),[q,setQ]=useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const ref=useRef(null);
  const triggerRef=useRef(null);
  const sel = multi ? (Array.isArray(value)?value:[]) : null;
  const filtered=options.filter(o=>o.toLowerCase().includes(q.toLowerCase()));
  useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target)&&!document.getElementById('sd-portal')?.contains(e.target)){setOpen(false);setQ('');}};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[]);
  const toggle=(o)=>{if(!multi)return;const has=sel.includes(o);onChange(has?sel.filter(x=>x!==o):[...sel,o]);};

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const panelH = 260;
      if (spaceBelow >= panelH || spaceBelow >= rect.top) {
        setPanelStyle({ position:'fixed', top: rect.bottom + 3, left: rect.left, width: rect.width, zIndex: 99999 });
      } else {
        setPanelStyle({ position:'fixed', bottom: window.innerHeight - rect.top + 3, left: rect.left, width: rect.width, zIndex: 99999 });
      }
    }
    setOpen(v=>!v);
    setQ('');
  };

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow >= 260 || spaceBelow >= rect.top) {
        setPanelStyle(s => ({ ...s, top: rect.bottom + 3, left: rect.left, width: rect.width, bottom: undefined }));
      } else {
        setPanelStyle(s => ({ ...s, bottom: window.innerHeight - rect.top + 3, left: rect.left, width: rect.width, top: undefined }));
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [open]);

  const MAX_DISPLAY = 2;
  const displayText = multi
    ? (sel.length===0 ? '' : sel.length<=MAX_DISPLAY ? sel.join(', ') : `${sel.slice(0,MAX_DISPLAY).join(', ')} (+${sel.length-MAX_DISPLAY} more)`)
    : (value||'');

  const panel = open ? createPortal(
    <div id="sd-portal" style={{...panelStyle, background:'#fff', border:'1px solid #ced4da', borderRadius:'6px', boxShadow:'0 4px 16px rgba(0,0,0,.14)', overflow:'hidden'}}>
      <div style={{padding:'8px 10px',borderBottom:'1px solid #e9ecef',background:'#f8f9fa'}}>
        <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Type to search..."
          style={{width:'100%',height:'34px',fontSize:'16px',padding:'0 8px',border:'1px solid #ced4da',borderRadius:'4px',outline:'none',background:'#fff',boxSizing:'border-box'}}/>
      </div>
      <div style={{maxHeight:'200px',overflowY:'auto'}}>
        {filtered.length===0
          ? <div style={{padding:'10px',fontSize:'12px',color:'#adb5bd',textAlign:'center'}}>No options</div>
          : filtered.map((o,i)=>{
              const checked=multi&&sel.includes(o);
              return<div key={i} onClick={()=>{if(multi){toggle(o);}else{onChange(o);setOpen(false);setQ('');}}}
                style={{padding:'8px 12px',fontSize:'13px',cursor:'pointer',color:'#212529',background:checked?BRAND_LIGHT:'#fff',display:'flex',alignItems:'center',gap:'8px',borderBottom:'1px solid #f1f3f4'}}
                onMouseEnter={e=>{if(!checked)e.currentTarget.style.background='#f0fdf8';}}
                onMouseLeave={e=>{e.currentTarget.style.background=checked?BRAND_LIGHT:'#fff';}}>
                {multi&&<input type="checkbox" checked={checked} onChange={()=>{}} style={{accentColor:BRAND,flexShrink:0,cursor:'pointer'}}/>}
                {o}
              </div>;
            })
        }
      </div>
      {multi&&sel.length>0&&(
        <div onClick={()=>onChange([])}
          style={{padding:'7px 12px',borderTop:'1px solid #e9ecef',fontSize:'11px',color:'#dc3545',cursor:'pointer',fontWeight:600,background:'#fff',textAlign:'center'}}
          onMouseEnter={e=>e.currentTarget.style.background='#fff5f5'}
          onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
          ✕ Clear all
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return(
    <div ref={ref} style={{position:'relative'}}>
      <div ref={triggerRef} onClick={handleOpen}
        style={{minHeight:'34px',border:'1px solid #dee2e6',borderRadius:'5px',display:'flex',alignItems:'center',padding:'4px 10px',cursor:'pointer',background:'#fff',justifyContent:'space-between',fontSize:'13px',gap:'6px'}}>
        <span style={{color:'#212529',flex:1,wordBreak:'break-word',lineHeight:1.4,textAlign:align,fontWeight:bold?700:undefined}}>{displayText}</span>
        <span style={{color:'#adb5bd',fontSize:'11px',flexShrink:0}}>{open?'▲':'▼'}</span>
      </div>
      {panel}
    </div>
  );
}

// ── Date display: always MM/dd/yyyy (2026-10-08) ────────────────────────────
// Stored values stay ISO (yyyy-mm-dd) everywhere — saved data, PDF mapping and
// the `signedDateFor` auto-fill all depend on that — but nothing a person
// READS is ever shown in the browser's own locale format or as raw ISO.
// fmtUSDate: ISO (optionally with a time part) → MM/dd/yyyy; anything else is
// returned untouched. usToISO: MM/dd/yyyy (or ISO) → ISO, or '' if invalid.
const fmtUSDate = v => {
  if (typeof v !== 'string') return v;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : v;
};
const usToISO = v => {
  if (typeof v !== 'string') return '';
  let y, mo, d;
  let m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) { mo = +m[1]; d = +m[2]; y = +m[3]; }
  else if ((m = v.match(/^(\d{4})-(\d{2})-(\d{2})/))) { y = +m[1]; mo = +m[2]; d = +m[3]; }
  else return '';
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return '';
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};
// Text box with a MM/dd/yyyy mask plus a calendar button. Replaces the native
// <input type="date">, whose on-screen format follows each computer's locale
// (dd/mm/yyyy, yyyy-mm-dd, …) and can't be forced. `value`/`onChange` still
// speak ISO. The calendar popup is a throwaway native date input created on
// click (never left in the form DOM, so PDF/print code that scans the form
// for input[type=date] never sees a second, hidden one).
function UsDateInput({ value, onChange, style, readOnly = false, disabled = false, width }) {
  const [text, setText] = useState(() => fmtUSDate(usToISO(value) || value || ''));
  const lastEmitted = useRef(value || '');
  useEffect(() => {
    if ((value || '') === lastEmitted.current) return;
    lastEmitted.current = value || '';
    setText(fmtUSDate(usToISO(value) || value || ''));
  }, [value]);
  const emit = iso => { lastEmitted.current = iso; onChange(iso); };
  const handleText = e => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let t = digits;
    if (digits.length > 4) t = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) t = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(t);
    const iso = digits.length === 8 ? usToISO(t) : '';
    if (iso !== (value || '')) emit(iso);
  };
  // The calendar popup is opened from a REAL, always-laid-out native date
  // input that sits invisibly along the bottom edge of this box (not created on
  // the fly): Chrome anchors its popup to that input's on-screen rectangle, and
  // a throwaway element appended and opened in the same tick had no layout yet,
  // so the popup opened in the window's top-left corner (reported 2026-10-08,
  // twice). PDF/print code ignores it via the .frm-date-native class.
  const nativeRef = useRef(null);
  const openPicker = () => {
    if (readOnly || disabled) return;
    const el = nativeRef.current;
    if (!el) return;
    el.value = usToISO(text) || '';
    try {
      el.getBoundingClientRect(); // make sure layout is current before anchoring
      if (el.showPicker) el.showPicker(); else el.focus();
    } catch (_) { try { el.focus(); } catch (__) {} }
  };
  return (
    <span className="frm-date-wrap" style={{ position: 'relative', display: 'inline-block', width: width || '100%', maxWidth: '100%' }}>
      <input
        type="text" inputMode="numeric" autoComplete="off"
        className="frm-date-text"
        placeholder="mm/dd/yyyy"
        value={text}
        onChange={handleText}
        onBlur={() => setText(fmtUSDate(usToISO(value) || value || ''))}
        readOnly={readOnly} disabled={disabled}
        style={{ ...style, width: '100%', paddingRight: '28px', boxSizing: 'border-box' }}
      />
      {!readOnly && !disabled && (
        <input
          ref={nativeRef} type="date" className="frm-date-native" tabIndex={-1} aria-hidden="true"
          value={usToISO(text) || ''}
          onChange={e => { const next = e.target.value; setText(next ? fmtUSDate(next) : ''); emit(next); }}
          style={{ position: 'absolute', left: 0, bottom: 0, width: '100%', height: '1px', opacity: 0, pointerEvents: 'none', border: 0, padding: 0, margin: 0 }}
        />
      )}
      {!readOnly && !disabled && (
        <button type="button" className="frm-date-cal" tabIndex={-1} onClick={openPicker}
          title="Pick a date"
          style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '2px', color: '#6c757d' }}>📅</button>
      )}
    </span>
  );
}

// ── Inline segment renderer ───────────────────────────────────────────────────
function InlineSegRenderer({seg, value, onChange, prefillData={}, providerReadOnly=false, lockPrefilled=false}) {
  const todayLocal = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
  const inpStyle={height:'32px',fontSize:'16px',padding:'0 6px',border:'none',borderBottom:'1px solid #aaa',borderRadius:'0',outline:'none',background:'transparent',fontFamily:'inherit',verticalAlign:'middle',display:'inline-block',color:'#212529',WebkitTextFillColor:'#212529',colorScheme:'light',boxSizing:'border-box',maxWidth:'100%'};

  if(seg.segType==='text_label') { const segPrefillVal = seg.prefilled && seg.prefillKey && prefillData && typeof prefillData[seg.prefillKey]!=='object' && prefillData[seg.prefillKey]!=null && prefillData[seg.prefillKey]!=='' ? prefillData[seg.prefillKey] : null;
    const hasBg = seg.bgColor && seg.bgColor !== 'transparent';
    return(
    <span style={{fontSize:seg.fontSize?`${seg.fontSize}px`:'14px',color:seg.color||'#212529',fontWeight:seg.bold?700:400,fontStyle:seg.italic?'italic':'normal',textDecoration:seg.underline?'underline':'none',background:hasBg?seg.bgColor:'transparent',padding:hasBg?'2px 6px':0,borderRadius:hasBg?'3px':0,textAlign:seg.align||'left',display:'inline-block',width:seg.labelWidth?`${seg.labelWidth}px`:'auto'}}>{segPrefillVal!=null?segPrefillVal:seg.text}</span>
  ); }

  const ftype=seg.fieldType;
  const val = value || '';
  const prefilled = seg.prefilled && seg.prefillKey && prefillData[seg.prefillKey];
  const w = seg.segWidth && seg.segWidth!=='auto' ? `${seg.segWidth}px` : '160px';

  if(ftype==='date') return(
    <UsDateInput value={val||''} onChange={onChange} style={inpStyle} width={w}/>
  );
  if(ftype==='provider_dropdown') {
    // Same data source as the standalone provider_dropdown field. The segment's
    // value is the option's DISPLAY text (not its key) so narrative / print-
    // filled-only / PDF output read it as ordinary text; the Intake wrapper maps
    // it back to the provider key when it changes.
    const opts = Array.isArray(prefillData.provider_list) ? prefillData.provider_list : [];
    const currentLabel = opts.find(o => String(o.key) === String(prefillData.provider_id))?.value || '';
    const shown = val || currentLabel;
    const pw = seg.segWidth && seg.segWidth!=='auto' ? `${seg.segWidth}px` : '260px';
    if (providerReadOnly) return <span style={{...inpStyle,width:pw,display:'inline-flex',alignItems:'center',color:'#495057',fontWeight:seg.valueBold?700:undefined}}>{shown}</span>;
    return (
      <span style={{display:'inline-block',width:pw,maxWidth:'100%',verticalAlign:'middle'}}>
        <SearchDropdown options={opts.map(o=>o.value)} value={shown} onChange={onChange} bold={!!seg.valueBold}/>
      </span>
    );
  }
  if(ftype==='dropdown') return(
    <select value={val} onChange={e=>onChange(e.target.value)} style={{...inpStyle,width:w,cursor:'pointer',background:'transparent'}}>
      <option value="">Select...</option>{(seg.options||[]).map((o,i)=><option key={i}>{o}</option>)}
    </select>
  );
  if(ftype==='radio') return(
    <span style={{display:'inline-flex',gap:'10px',verticalAlign:'middle',flexWrap:'wrap'}}>
      {(seg.options||[]).map((o,i)=><label key={i} style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:seg.optionFontSize?`${seg.optionFontSize}px`:'13px',cursor:'pointer'}}><input type="radio" name={seg.id} checked={val===o} onChange={()=>{}} onClick={()=>onChange(val===o?'':o)} style={{accentColor:BRAND}}/>{o}</label>)}
    </span>
  );
  if(ftype==='checkbox') return(
    <span style={{display:'inline-flex',gap:'10px',verticalAlign:'middle',flexWrap:'wrap'}}>
      {(seg.options||[]).map((o,i)=>{const checked=(val||[]).includes(o);return<label key={i} style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:seg.optionFontSize?`${seg.optionFontSize}px`:'13px',cursor:'pointer'}}><input type="checkbox" checked={checked} onChange={()=>{const curr=val||[];onChange(checked?curr.filter(x=>x!==o):[...curr,o]);}} style={{accentColor:BRAND}}/>{o}</label>;})}
    </span>
  );
  if(ftype==='single_checkbox') return(
    <label style={{display:'inline-flex',alignItems:'center',gap:'6px',fontSize:seg.optionFontSize?`${seg.optionFontSize}px`:'13px',cursor:'pointer',verticalAlign:'middle'}}>
      <input type="checkbox" checked={!!val} onChange={e=>onChange(e.target.checked)} style={{accentColor:BRAND}}/><span style={{width:seg.labelWidth?`${seg.labelWidth}px`:'auto',display:'inline-block'}}>{seg.label||seg.placeholder}</span>
    </label>
  );
  // text / number / email / phone
  // The actual response (val) wins over the auto-filled default (prefilled
  // ? prefillData[...]) whenever one exists — otherwise this controlled
  // input would reset to the prefilled value on every render, silently
  // undoing anything the user types. Auto-filled is a starting value, not
  // a lock, so there's no readOnly here either.
  return(
    <input type={ftype==='email'?'email':ftype==='number'?'number':ftype==='phone'?'tel':'text'}
      value={fmtUSDate(val || (prefilled ? prefillData[seg.prefillKey] : ''))}
      onChange={e=>{
        let v = e.target.value;
        if (ftype==='number' && seg.max!=null && v!=='' && !isNaN(parseFloat(v)) && parseFloat(v) > seg.max) v = String(seg.max);
        onChange(v);
      }}
      placeholder={seg.placeholder}
      readOnly={lockPrefilled && !!prefilled}
      max={ftype==='number'?seg.max:undefined}
      style={{...inpStyle, width:w, background:(lockPrefilled && prefilled)?'#f8f9fa':'transparent', color:(lockPrefilled && prefilled)?'#495057':'#212529'}}/>
  );
}

// Helper: inject inline styles on ul/ol/li so bullets always render correctly
function fixListHtml(html) {
  return (html||'')
    .replace(/<ul/gi, '<ul style="padding-left:22px;margin:4px 0;list-style-type:disc;"')
    .replace(/<ol/gi, '<ol style="padding-left:22px;margin:4px 0;list-style-type:decimal;"')
    .replace(/<li/gi, '<li style="margin:2px 0;display:list-item;"');
}

// ── RichTextArea for renderer — ref-based to preserve cursor position ─────────
// ── Speech-to-text — shared by both rich text editors (this file's runtime
// RichTextArea and FormBuilderEditor.js's builder-time RichTextEditor).
// Exported rather than duplicated in each file, since we've already been
// bitten once this conversation by two copies of the same logic drifting
// apart (see mergeAttachmentsIntoPdf). Uses the browser's native
// SpeechRecognition API — no external service, so it only works in
// browsers that implement it (Chrome/Edge; not Firefox/Safari as of this
// writing). The mic button hides itself entirely when unsupported rather
// than rendering a dead control.
export function useSpeechToText(onTranscript) {
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const [listening, setListening] = useState(false);
  const SpeechRecognitionAPI = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = () => {
    shouldListenRef.current = false;
    recognitionRef.current?.stop();
  };

  const start = () => {
    if (!SpeechRecognitionAPI) return;
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    // interimResults:true — see onresult below for why this is now required
    // rather than just a nice-to-have live preview.
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      // Bug found 2026-10-05 ("the mic listens to the same thing N number of
      // times"): the previous version trusted e.resultIndex to mark exactly
      // where the NEW part of the transcript started, sliced from there, and
      // INSERTED that slice. That assumes a result at a given index is fixed
      // forever once it's fired — but Chrome's continuous-mode recognizer
      // doesn't actually guarantee that: it can fire another onresult event
      // that revises or extends a stretch of speech it already reported,
      // without that being visible from resultIndex alone. Every time that
      // happened, the "new" slice wasn't actually new, so it got inserted on
      // top of what was already there — and because each revision tends to
      // be a longer version of the last one, the duplication compounds with
      // every word (reproduced from the user's screenshot: "Yes Yeah sure
      // Yeah sure Yeah sure last Yeah sure last of the ...").
      //
      // Fix: report the FULL transcript-so-far for this recognition session
      // on every event (every result currently in e.results, not just the
      // ones from resultIndex on) and let the consumer REPLACE its on-screen
      // dictation text wholesale each time via useDictationTranscriptHandler
      // below — correct regardless of whether the recognizer appended,
      // revised, or left alone any earlier part of the utterance, since
      // there's no delta calculation left to get wrong.
      const full = Array.from(e.results).map(r => r[0].transcript).join(' ').trim();
      onTranscript(full, false);
    };
    recognition.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        alert('Microphone access was blocked. Please allow microphone permissions to use speech-to-text.');
        shouldListenRef.current = false;
      }
      // 'no-speech' and other transient errors: let onend decide whether to restart.
    };
    recognition.onend = () => {
      // A restart below starts a BRAND NEW recognition session with its own
      // fresh e.results indexing from zero — whatever text the session that
      // just ended produced needs to be locked in as permanent content now,
      // via the onTranscript(null, true) "finalize" signal, so the next
      // session's replacements don't reach back and touch it.
      onTranscript(null, true);
      // Some browsers stop recognition after a pause of silence even with
      // continuous:true — restart automatically unless the user explicitly
      // stopped, so dictation doesn't cut out mid-thought.
      if (shouldListenRef.current) {
        try { recognition.start(); } catch { /* already starting */ }
      } else {
        setListening(false);
      }
    };
    recognitionRef.current = recognition;
    shouldListenRef.current = true;
    setListening(true);
    recognition.start();
  };

  const toggle = () => { listening ? stop() : start(); };

  useEffect(() => () => { shouldListenRef.current = false; recognitionRef.current?.stop(); }, []); // eslint-disable-line

  return { listening, toggle, supported: !!SpeechRecognitionAPI };
}

export function MicButton({ listening, onClick, supported }) {
  if (!supported) return null;
  return (
    <button
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      title={listening ? 'Stop dictation' : 'Start dictation'}
      style={{
        width: '26px', height: '22px', border: `1px solid ${listening ? '#dc3545' : '#dee2e6'}`,
        borderRadius: '3px', background: listening ? '#fdeaea' : '#fff',
        color: listening ? '#dc3545' : '#495057', cursor: 'pointer', fontSize: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >🎤</button>
  );
}

// ── Shared dictation-insertion handler for RichTextArea (this file) and
// FormBuilderEditor.js's RichTextEditor. Pass its return value as
// useSpeechToText's onTranscript. Keeps a single "live" marker <span> inside
// the contentEditable whose content gets fully REPLACED on every (text,
// false) update within one recognition session — required by
// useSpeechToText's replace-not-append contract above — and is locked in as
// permanent plain content (marker forgotten, left as normal DOM text) the
// moment a (null, true) "finalize" signal arrives, so the NEXT session
// starts its own fresh marker after it rather than overwriting old dictated
// text. `onChange` here is whatever the consumer needs to run after every
// DOM update (saving innerHTML, and in FormBuilderEditor.js's case also
// setting its own skip-external-sync flag) — call signature `(html) => void`.
export function useDictationTranscriptHandler(ref, onChange) {
  const markerRef = useRef(null);
  return (text, final) => {
    if (final) {
      const marker = markerRef.current;
      markerRef.current = null;
      if (marker && ref.current && ref.current.contains(marker)) {
        const trimmed = marker.textContent.replace(/\s+$/, '');
        if (!trimmed) {
          marker.remove();
        } else {
          // A plain trailing space inside the marker's own textContent isn't
          // reliable here — the next dictation session's insertHTML (below)
          // can land right at this boundary and the browser's contentEditable
          // whitespace normalization can silently drop it, gluing the two
          // sessions' text together with no visible gap (e.g. "...the" +
          // "So..." -> "...theSo..."). A non-breaking space as a separate
          // sibling text node survives that normalization.
          marker.textContent = trimmed;
          marker.removeAttribute('data-dictation-live');
          const sep = document.createTextNode(' ');
          marker.after(sep);
          // Move the caret past the separator too — otherwise it's left
          // sitting BEFORE it (where the marker used to end), and the next
          // dictation session's insertHTML would land there instead of
          // after it, putting the separator in the wrong place entirely.
          const sel = window.getSelection();
          if (sel) {
            const range = document.createRange();
            range.setStartAfter(sep);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
        onChange(ref.current.innerHTML || '');
      }
      return;
    }
    if (!ref.current) return;
    ref.current.focus();
    if (!markerRef.current || !ref.current.contains(markerRef.current)) {
      const id = `dct-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      document.execCommand('insertHTML', false, `<span id="${id}" data-dictation-live="1"></span>`);
      markerRef.current = ref.current.querySelector(`#${id}`);
      if (markerRef.current) markerRef.current.removeAttribute('id');
    }
    if (markerRef.current) {
      markerRef.current.textContent = text ? text + ' ' : '';
      // Caret goes just after the marker so normal typing/toolbar commands
      // continue naturally from where the dictated text currently ends.
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.setStartAfter(markerRef.current);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
    onChange(ref.current.innerHTML || '');
  };
}

function RichTextArea({ field, value, onChange, align, labelEl, readOnly=false }) {
  const ref = useRef(null);
  const isMounted = useRef(false);
  const [fmt, setFmt] = useState({bold:false,italic:false,underline:false});
  const [fontSize, setFontSize] = useState('3');
  const speech = useSpeechToText(useDictationTranscriptHandler(ref, html => onChange(html)));

  // Set innerHTML on mount
  useEffect(() => {
    if (ref.current && !isMounted.current) {
      ref.current.innerHTML = fixListHtml(value || field.content || '');
      isMounted.current = true;
    }
  }, []); // eslint-disable-line

  // Update innerHTML only when changed externally — skip if editor is focused (user typing)
  useEffect(() => {
    if (ref.current && isMounted.current && document.activeElement !== ref.current) {
      const incoming = fixListHtml(value || field.content || '');
      if (ref.current.innerHTML !== incoming) ref.current.innerHTML = incoming;
    }
  }, [value]); // eslint-disable-line

  const updateFmt = () => setFmt({
    bold:      document.queryCommandState('bold'),
    italic:    document.queryCommandState('italic'),
    underline: document.queryCommandState('underline'),
  });

  const exec = (cmd, val=null) => {
    // Save selection before execCommand so cursor position is preserved
    const sel = window.getSelection();
    const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    ref.current?.focus();
    if (range) { sel.removeAllRanges(); sel.addRange(range); }
    document.execCommand(cmd, false, val);
    onChange(ref.current?.innerHTML || '');
    updateFmt();
  };

  const tbBtn = (active, label, cmd, val=null) => (
    <button
      onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}
      style={{
        width:'26px', height:'22px', border:`1px solid ${active?BRAND:'#dee2e6'}`,
        borderRadius:'3px', background:active?BRAND_LIGHT:'#fff',
        color:active?BRAND:'#495057', cursor:'pointer', fontSize:'11px',
        fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
      }}
    >{label}</button>
  );

  const sep = <div style={{width:'1px',background:'#dee2e6',margin:'0 2px'}}/>;

  return (
    <div>
      {labelEl}
      <div style={{border:'1px solid #dee2e6', borderRadius:'5px', overflow:'hidden'}}>
        {!readOnly && (
          <div className="rta-toolbar" style={{gap:'3px',padding:'4px 6px',background:'#f8f9fa',borderBottom:'1px solid #dee2e6',flexWrap:'wrap'}}>
            {tbBtn(fmt.bold,      <b>B</b>,  'bold')}
            {tbBtn(fmt.italic,    <i>I</i>,  'italic')}
            {tbBtn(fmt.underline, <u>U</u>,  'underline')}
            {sep}
            {tbBtn(false, 'L', 'justifyLeft')}
            {tbBtn(false, 'C', 'justifyCenter')}
            {tbBtn(false, 'R', 'justifyRight')}
            {sep}
            {tbBtn(false, '☰', 'insertUnorderedList')}
            {tbBtn(false, '1.', 'insertOrderedList')}
            {sep}
            <select
              value={fontSize}
              onMouseDown={e => e.stopPropagation()}
              onChange={e => {
                const v = e.target.value;
                setFontSize(v);
                ref.current?.focus();
                if (v === '3') { document.execCommand('removeFormat', false, null); }
                else { document.execCommand('fontSize', false, v); }
                onChange(ref.current?.innerHTML || '');
                updateFmt();
              }}
              style={{height:'26px',fontSize:'16px',border:'1px solid #dee2e6',borderRadius:'3px',background:'#fff',cursor:'pointer'}}
            >
              <option value="1">Small</option>
              <option value="3">Normal</option>
              <option value="5">Large</option>
              <option value="7">XL</option>
            </select>
            {speech.supported && sep}
            <MicButton listening={speech.listening} onClick={speech.toggle} supported={speech.supported}/>
          </div>
        )}
        <div
          ref={ref}
          className="frm-rta-box"
          contentEditable={!readOnly}
          suppressContentEditableWarning
          onInput={e => !readOnly && onChange(e.currentTarget.innerHTML)}
          onKeyUp={updateFmt}
          onMouseUp={updateFmt}
          style={{
            height:`${field.boxHeight||120}px`, minHeight:'40px',
            overflowY:'auto', resize:'vertical', background:readOnly?'#f8f9fa':'#fff',
            fontSize:'14px', lineHeight:1.6, padding:'8px 10px', color:'#212529',
            cursor:readOnly?'default':'text', textAlign:align, outline:'none',
            colorScheme:'light', WebkitTextFillColor:'#212529',
          }}
        />
      </div>
    </div>
  );
}

// ── Single field renderer ─────────────────────────────────────────────────────
function FieldRenderer({field, value, onChange, prefillData={}, responses={}, mobile=false, readOnly=false, providerDropdownReadOnly=false, onSignatureClick=null, suppressedFieldTypes=[], setVal=null, formName='', disabledPrefilled=false, suppressedFieldIds=[]}) {
  // Hooks must run unconditionally on every call regardless of field type or
  // the early returns below (suppressedFieldTypes, layout types, etc.) — only
  // used by the isSig() branch further down (which Draw/Type tab is active
  // for an as-yet-empty signature; see isDrawnSigValue's comment there).
  const [sigModeState, setSigModeState] = useState(null);
  // Remembers each Draw/Type mode's own last value while the user flips the
  // toggle (2026-10-07): a drawn signature and a typed name share ONE value
  // slot, so the old "clear on switch" behavior permanently threw away the
  // drawn signature the moment someone peeked at Type — and never put the
  // physician's name into Type either. Per-field-instance only (not saved).
  const sigStashRef = useRef({ draw: '', type: '' });
  // Type (e-signature) mode on a Patient Signature box fills with the patient's
  // name — once per field instance, so Clear / editing the name isn't undone.
  // Covers both the Draw/Type toggle (sigModeState==='type') and boxes locked
  // to Type Only in the builder. Runs again when prefillData resolves late.
  useEffect(() => {
    if (field.type !== 'sig_patient' || readOnly || value) return;
    if (!(field.signatureMode === 'type' || sigModeState === 'type')) return;
    if (sigStashRef.current.autoFilled) return;
    const nm = resolvePatientSigName(field, prefillData);
    if (nm) { sigStashRef.current.autoFilled = true; onChange(nm); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.type, field.signatureMode, sigModeState, prefillData, value]);
  if (suppressedFieldTypes.includes(field.type)) return null;
  if (suppressedFieldIds.includes(field.id)) return null; // hideInPatientApp
  // Patient app: auto-filled patient data is locked (disabledPrefilled).
  const lockPre = !!disabledPrefilled && !!field.prefilled;
  const align=field.align||'left';
  const inp={height:'38px',fontSize:'16px',padding:'0 10px',border:'1px solid #dee2e6',borderRadius:'5px',width:'100%',maxWidth:'100%',boxSizing:'border-box',outline:'none',fontFamily:'inherit',color:'#212529',background:'#fff',colorScheme:'light',WebkitTextFillColor:'#212529'};

  // ── Template variable resolver — replaces {{key}} with live values ────────
  // Checks: 1) current field responses, 2) prefillData scalars
  const resolveVars = (str) => {
    if (!str || !str.includes('{{')) return str;
    return str.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      // First check prefillData (provider_name, patient_name, etc.)
      if (prefillData[key] !== undefined && typeof prefillData[key] !== 'object') return prefillData[key];
      // Then check responses (other field values by prefillKey)
      // Find any field whose prefillKey matches
      return responses[key] ?? _;
    });
  };
  const focusBorder={borderColor:BRAND,boxShadow:`0 0 0 2px ${BRAND_LIGHT}`};

  // Layout types — no value, no onChange
  if(field.type==='heading') {
    const hasBg = field.bgColor && field.bgColor !== 'transparent';
    return <div style={{
      fontSize:field.level==='h2'?'18px':field.level==='h4'?'13px':'15px',
      fontWeight:700,color:'#212529',textAlign:align,
      padding: hasBg ? '6px 10px' : '2px 0',
      background: hasBg ? field.bgColor : 'transparent',
      borderRadius: hasBg ? '5px' : 0,
    }}>{resolveVars(field.label)}</div>;
  }
  if(field.type==='section_start') {
    if (!field.label || !field.label.trim()) return null; // pure boundary marker, no visible content
    const hasBg = field.bgColor && field.bgColor !== 'transparent';
    // Tighter than the plain `heading` field's padding/line-height (6px/no
    // line-height set) — a section title bar reads better compact, closer to
    // an accordion header's height, especially now that a collapsible one
    // also carries the +/- indicator alongside it (see frm-section-toggle).
    return <div style={{
      fontSize:field.level==='h2'?'18px':field.level==='h4'?'13px':'15px',
      fontWeight:700,color:'#212529',textAlign:align,lineHeight:1.3,
      padding: hasBg ? '5px 10px' : '2px 0',
      background: hasBg ? field.bgColor : 'transparent',
      borderRadius: hasBg ? '5px' : 0,
    }}>{resolveVars(field.label)}</div>;
  }
  if(field.type==='section_end') return null; // pure boundary marker, no visible content
  if(field.type==='tab_break') return null; // pure boundary marker — its label feeds the tab bar button, never rendered inline
  if(field.type==='divider') return <hr style={{border:'none',borderTop:`${field.dividerHeight||1}px ${field.dividerStyle||'solid'} ${field.dividerColor||'#dee2e6'}`,margin:'4px 0'}}/>;
  if(field.type==='page_break') return <div className="frm-page-break" style={{pageBreakBefore:'always',breakBefore:'page',display:'flex',alignItems:'center',gap:'8px',margin:'10px 0'}}><div style={{flex:1,borderTop:'2px dashed #f0a500'}}/><span style={{fontSize:'9px',fontWeight:700,color:'#f0a500',background:'#fff8e6',padding:'2px 8px',borderRadius:'20px',whiteSpace:'nowrap'}}>✂ NEW PAGE STARTS HERE</span><div style={{flex:1,borderTop:'2px dashed #f0a500'}}/></div>;
  if(field.type==='label')   {
    const lpv=field.prefilled&&field.prefillKey&&prefillData&&typeof prefillData[field.prefillKey]!=='object'&&prefillData[field.prefillKey]!=null&&prefillData[field.prefillKey]!==''?prefillData[field.prefillKey]:null;
    const hasBg = field.bgColor && field.bgColor !== 'transparent';
    return <div style={{
      fontSize:`${field.labelSize||14}px`,fontWeight:field.labelBold?700:400,
      fontStyle:field.labelItalic?'italic':'normal',textDecoration:field.labelUnderline?'underline':'none',
      color:field.labelColor||'#212529',textAlign:align,
      padding: hasBg ? '5px 9px' : 0,
      background: hasBg ? field.bgColor : 'transparent',
      borderRadius: hasBg ? '4px' : 0,
    }}>{lpv!=null?lpv:resolveVars(field.labelText)}</div>;
  }
  if(field.type==='paragraph') {
    const rawContent = resolveVars(field.content || '');
    const fixedHtml = rawContent
      .replace(/<ul/gi, '<ul style="padding-left:22px;margin:4px 0;list-style-type:disc;"')
      .replace(/<ol/gi, '<ol style="padding-left:22px;margin:4px 0;list-style-type:decimal;"')
      .replace(/<li/gi, '<li style="margin:2px 0;display:list-item;"');
    return(
      <div style={{fontSize:'14px',color:'#212529',lineHeight:1.7,background:field.bgColor==='green'?BRAND_LIGHT:'#fff',padding:'10px 12px',borderRadius:'5px',border:field.bgColor==='green'?`1px solid ${BRAND}`:'1px solid #e9ecef',textAlign:align,boxSizing:'border-box',width:'100%',overflowWrap:'break-word',wordBreak:'break-word'}}
        dangerouslySetInnerHTML={{__html:fixedHtml}}/>
    );
  }

  if(field.type==='inline_row') return(
    <div className="frm-inline-row" style={{display:'flex',flexWrap:'wrap',alignItems:'center',rowGap:'6px',columnGap:'8px',color:'#212529',minHeight:'34px'}}>
      {(field.segments||[]).map((seg,i)=>(
        <InlineSegRenderer key={seg.id||i} seg={seg} value={(value||{})[seg.id||i]} onChange={v=>onChange({...(value||{}),[seg.id||i]:v})} prefillData={prefillData} providerReadOnly={providerDropdownReadOnly || disabledPrefilled} lockPrefilled={!!disabledPrefilled}/>
      ))}
    </div>
  );

  // Container: side-by-side columns, each holding its own independent set of
  // fields. Nested fields are full field objects with their own ids — their
  // values live in the SAME flat top-level `responses` object as everything
  // else (not nested inside the container's own value), so setVal is reused
  // directly rather than inventing a separate nested-value scheme.
  if(field.type==='container') {
    const cols = field.columns || [];
    return (
      <div className="frm-container-row" style={{display:'flex',flexWrap:'wrap',gap:'12px',alignItems:'flex-start'}}>
        {cols.map(col=>{
          const colRows = buildRows(col.fields||[]);
          return (
            <div key={col.id} style={{width:`${col.widthPct||50}%`,minWidth:0,boxSizing:'border-box'}}>
              {colRows.map((row,ri)=>(
                <div key={ri} className="frm-row">
                  {row.map(nf=>{
                    const w = nf.type==='container' ? '100%' : getW(nf, mobile);
                    return (
                      <div key={nf.id} style={{width:w}} className="frm-field-wrap" data-field-id={nf.id} data-field-type={nf.type}>
                        <FieldRenderer
                          field={nf}
                          value={responses[nf.id]}
                          onChange={v=>setVal&&setVal(nf.id, v, nf)}
                          prefillData={prefillData}
                          responses={responses}
                          mobile={mobile}
                          readOnly={readOnly}
                          providerDropdownReadOnly={providerDropdownReadOnly}
                          onSignatureClick={onSignatureClick}
                          suppressedFieldTypes={suppressedFieldTypes}
                          setVal={setVal}
                          formName={formName}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  if(isSig(field.type)) {
    const sigLabelFromForm = field.label === '' ? null : (field.label || SIG_LABELS[field.type] || null);
    // Doctor Signature ignores the form-configured label in favor of the
    // signing physician's own title (see getDoctorTitleSignature above) —
    // falling back to the form's own label only when no title is resolvable.
    const sigLabelText = field.type === 'sig_doctor' ? getDoctorTitleSignature(sigLabelFromForm) : sigLabelFromForm;
    // signatureMode is 'draw' | 'type' | 'both' (default — exact prior
    // behavior, untouched for every existing field that predates this).
    // Computed up here (rather than only in the onSignatureClick===null
    // branch below, where it originally lived) because a field locked to
    // Type Only must bypass the external signing popup entirely — added
    // 2026-10-05 after Ajay found that a Medical Notes field wired to
    // DoctorSign (via onSignatureClick, see MedicalNoteFormBuilder.js)
    // kept opening the draw-signature popup and showing "✓ Signature
    // captured" no matter what Signature Mode was set to in the builder:
    // that popup branch was never checking signatureMode at all.
    const forcedMode = field.signatureMode === 'draw' ? 'draw' : field.signatureMode === 'type' ? 'type' : null;
    // Draw / Type mode + toggle helpers are computed BEFORE the popup branch
    // (2026-10-07) so a host that wires onSignatureClick (Medical Notes →
    // DoctorSign) still gets the Draw / Type (e-signature) toggle when the
    // builder left Signature Mode on "Both". Previously the popup branch
    // returned early with no toggle, so Medical Notes showed only the draw
    // box even though Intake sheets and Builder Preview showed both.
    const sigMode = forcedMode || sigModeState || (isDrawnSigValue(value) ? 'draw' : (value ? 'type' : 'draw'));
    const setSigMode = m => {
      if (m === sigMode) return;
      // Stash what's in the box under the mode it belongs to, then restore
      // the target mode's own last value instead of blanking it.
      if (value) sigStashRef.current[isDrawnSigValue(value) ? 'draw' : 'type'] = value;
      let next = sigStashRef.current[m] || '';
      // First visit to Type on a Doctor signature: fill the signing
      // physician's name, same as the form's initial auto-fill does.
      if (m === 'type' && !next && field.type === 'sig_doctor' && field.prefilled !== false) {
        const nm = resolveSigDoctorName(field, prefillData);
        if (nm !== undefined) next = nm;
      }
      setSigModeState(m);
      onChange(next);
    };
    const toggleBtnStyle = active => ({
      fontSize:'11px', fontWeight:600, padding:'3px 12px', cursor:'pointer',
      border:'1px solid ' + (active ? BRAND : '#dee2e6'),
      background: active ? BRAND_LIGHT : '#fff',
      color: active ? BRAND : '#6c757d',
      borderRadius:'4px',
    });
    const sigModeToggle = !forcedMode && (
      <div className="sig-mode-toggle" style={{display:'flex',gap:'6px',marginBottom:'5px'}}>
        <button type="button" style={toggleBtnStyle(sigMode==='draw')} onClick={()=>setSigMode('draw')}>Draw</button>
        <button type="button" style={toggleBtnStyle(sigMode==='type')} onClick={()=>setSigMode('type')}>Type (e-signature)</button>
      </div>
    );
    if (onSignatureClick && sigMode !== 'type') {
      // Host provided a popup handler — show the canvas as display-only and let
      // clicking it open the external DoctorSign popup rather than free-drawing.
      // Untouched by the Draw/Type toggle below — that's a separate, purely
      // in-app signing path this integration doesn't go through. Skipped when
      // forcedMode==='type' (see comment above) — DoctorSign is a drawing
      // popup, so a field locked to Type Only renders the typed input in the
      // form itself instead, same as a host with no popup integration at all.
      const sigPad = (
        <div style={{position:'relative'}}>
          <canvas
            width={600} height={160}
            className="sig-canvas"
            style={{border:'1px solid #dee2e6',borderRadius:'5px',background:'#fff',width:'100%',height:'160px',display:'block',cursor:'pointer'}}
            ref={el=>{
              if(el&&value){const img=new Image();img.onload=()=>{const ctx=el.getContext('2d');ctx.clearRect(0,0,el.width,el.height);ctx.drawImage(img,0,0,el.width,el.height);};img.src=value;}
              else if(el&&!value){const ctx=el.getContext('2d');ctx.clearRect(0,0,el.width,el.height);}
            }}
          />
          <div onClick={()=>onSignatureClick(field)} style={{position:'absolute',inset:0,cursor:'pointer',borderRadius:'5px',background:'transparent'}}/>
        </div>
      );
      return(
        <div>
          {sigLabelText && <div className="sig-label" style={{fontSize:'12px',fontWeight:600,color:'#212529',marginBottom:'5px',textAlign:align}}>{sigLabelText}</div>}
          {sigModeToggle}
          {sigPad}
          {value&&<div className="sig-captured" style={{fontSize:'10px',color:BRAND,marginTop:'3px',textAlign:'center'}}>✓ Signature captured</div>}
        </div>
      );
    }
    // ── Draw / Type toggle — added 2026-09-30 alongside the existing
    // hand-drawn SignaturePad, per explicit instruction not to remove it:
    // "dont remove current signature pad at all just add electronic
    // signature". A typed signature and a drawn one share the same plain-
    // string value slot (see isDrawnSigValue above for how they're told
    // apart), so which tab is showing is inferred from the field's current
    // value whenever it's non-empty — only an EMPTY field needs its own
    // local "which tab is the user on" state, since an empty string gives no
    // signal either way. Switching tabs while a value from the other mode is
    // present clears it (a typed name can't be shown on a draw canvas or
    // vice versa) rather than leaving a stale, mode-mismatched value behind.
    // ── Locked signature mode — added 2026-10-06 per explicit feedback that
    // the Draw/Type choice only ever surfaced at fill-time (Preview/the real
    // form), with no way in the Builder to pin a field to just one of them —
    // so a form could end up showing both a hand-drawn mark AND separately-
    // displayed typed/auto-filled text, which read as redundant. (forcedMode
    // itself is now computed above, before the onSignatureClick branch —
    // see the comment there for why.)
    // A drawn signature's value is a data:image... URL — never hand that to
    // TypedSignature's plain text <input>, which would just print the raw
    // base64 string as garbled "text" (confirmed happening in production
    // 2026-10-06: a field switched to Type Only after already having a
    // hand-drawn value literally rendered "data:image/png;base64,..." in the
    // box). Treat a mode-mismatched drawn value as empty instead — the sig_
    // doctor auto-fill block above still gets first crack at setting a real
    // name into this same field during the responses-init pass; this is only
    // the last-resort guard against ever displaying raw image data as text.
    // PATIENT APP: Draw mode opens the legacy-style signature popup (host-provided
    // onSignatureClick, handled above, still wins). Type mode stays inline.
    if (!onSignatureClick && sigMode !== 'type') {
      return(
        <div>
          {sigLabelText && <div className="sig-label" style={{fontSize:'12px',fontWeight:600,color:'#212529',marginBottom:'5px',textAlign:align}}>{sigLabelText}</div>}
          {sigModeToggle}
          <SignatureBox value={value||''} onChange={v=>onChange(v)} title={sigLabelText||''} readOnly={!!readOnly}/>
          {value&&<div className="sig-captured" style={{fontSize:'10px',color:BRAND,marginTop:'3px',textAlign:'center'}}>✓ Signature captured</div>}
        </div>
      );
    }
    const typedValue = isDrawnSigValue(value) ? '' : (value || '');
    const sigPad = sigMode === 'type'
      ? <TypedSignature value={typedValue} onCapture={v=>onChange(v)}/>
      : <SignaturePad fieldId={field.id} value={value||''} onCapture={v=>onChange(v)}/>;
    return(
      <div>
        {sigLabelText && <div className="sig-label" style={{fontSize:'12px',fontWeight:600,color:'#212529',marginBottom:'5px',textAlign:align}}>{sigLabelText}</div>}
        {/* Toggle only shown when the builder left this field on "Both" — a
            field locked to Draw or Type always renders just that one input,
            in the Builder's own Preview and in the real running form alike. */}
        {sigModeToggle}
        {sigPad}
        {value && (
          <div className="sig-captured" style={{fontSize:'10px',color:BRAND,marginTop:'3px',textAlign:'center'}}>
            {sigMode==='type' ? '✓ Electronically signed' : '✓ Signature captured'}
          </div>
        )}
      </div>
    );
  }

  if(field.type==='single_checkbox') return(
    <label style={{display:'flex',alignItems:'center',gap:'9px',fontSize:field.optionFontSize?`${field.optionFontSize}px`:'13px',color:'#212529',cursor:'pointer',padding:'6px 0',justifyContent:align==='center'?'center':align==='right'?'flex-end':'flex-start'}}>
      <input type="checkbox" checked={!!value} onChange={e=>onChange(e.target.checked)} style={{accentColor:BRAND,width:'15px',height:'15px'}}/>
      {field.label}
    </label>
  );

  // Label above input
  const prefillVal = field.prefilled&&field.prefillKey && typeof prefillData[field.prefillKey] !== 'object' ? prefillData[field.prefillKey] : null;
  const labelEl = (
    <label style={{fontSize:'12px',fontWeight:600,color:'#212529',marginBottom:'5px',display:'block',textAlign:align}}>
      {field.label}{field.required&&<span style={{color:'#dc3545'}}> *</span>}
    </label>
  );

  // Wrap input with label — top (default) or left
  const wrapField = (inputEl) => {
    if (field.labelPosition !== 'left') {
      // Top position: textAlign controls both label and input content
      return <div style={{textAlign:align}}>{labelEl}{inputEl}</div>;
    }
    // Left position: label on left, input fills remaining space
    // align controls the text-align of the label within its container
    const labelTextAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
    return (
      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
        <label style={{fontSize:'12px',fontWeight:600,color:'#212529',textAlign:labelTextAlign,minWidth:field.labelWidth?`${field.labelWidth}px`:'110px',flexShrink:0,lineHeight:1.4}}>
          {field.label}{field.required&&<span style={{color:'#dc3545'}}> *</span>}
        </label>
        <div style={{flex:1,minWidth:0}}>{inputEl}</div>
      </div>
    );
  };

  if(field.type==='textarea') {
    // responses value takes priority (covers provider cascade overrides)
    // fall back to prefillData only when responses has nothing
    const prefillFallback = (field.prefilled && field.prefillKey && prefillData[field.prefillKey]) ? prefillData[field.prefillKey] : null;
    const taValue = (value !== null && value !== undefined && value !== '') ? value : (prefillFallback || '');
    // Auto-filled is a starting value, not a lock — the person filling the
    // form must still be able to correct/edit it. Only the form's overall
    // readOnly state (e.g. viewing an already-finalized note) locks this.
    return <RichTextArea field={field} value={taValue} onChange={onChange} align={align} labelEl={field.label?labelEl:null} readOnly={!!readOnly || lockPre}/>;
  }

if(field.type==='provider_dropdown') {
  const providerOptions = (field.prefillKey && Array.isArray(prefillData[field.prefillKey]))
    ? prefillData[field.prefillKey]
    : (field.options||[]).map(o=>({key:o,value:o}));
  const selectedVal = value || prefillData['provider_id'] || '';
  const selectedDisplay = providerOptions.find(o => o.key === selectedVal)?.value || '';
  const displayOptions = providerOptions.map(o => o.value);
  const handleChange = (displayVal) => {
    const found = providerOptions.find(o => o.value === displayVal);
    onChange(found?.key || displayVal);
  };

  // Doctor role: read-only — just show the selected provider name
  if (providerDropdownReadOnly || disabledPrefilled || providerOptions.length <= 1) {
    return wrapField(
      <div style={{...inp, display:'flex', alignItems:'center', justifyContent:align==='center'?'center':align==='right'?'flex-end':'flex-start', background:'#f8f9fa', color:'#495057', cursor:'default', userSelect:'none', fontWeight:field.valueBold?700:undefined}}>
        {selectedDisplay}
      </div>
    );
  }

  // Searchable dropdown for all other roles
  return wrapField(
    <SearchDropdown
      options={displayOptions}
      value={selectedDisplay}
      onChange={handleChange}
      align={align}
      bold={!!field.valueBold}
    />
  );
}

  if(field.type==='dropdown') {
    const rawOpts = (field.prefilled && field.prefillKey && Array.isArray(prefillData[field.prefillKey]))
      ? prefillData[field.prefillKey]
      : (field.options || []);
    const normOpts = rawOpts.map((o, i) =>
      typeof o === 'string' ? {key: o, display: o} : {key: o.key ?? String(i), display: o.value || o.key || ''}
    );
    const isProviderList = field.prefillKey === 'provider_list';
    const isDisabled = isProviderList && providerDropdownReadOnly;
    return wrapField(
      <div>
        <select
          value={value!==null&&value!==undefined&&value!==''?value:(prefillVal!==null&&prefillVal!==undefined?prefillVal:'')}
          onChange={e=>onChange(e.target.value)}
          disabled={isDisabled}
          style={{...inp,textAlign:align,textAlignLast:align,background:isDisabled?'#f8f9fa':'#fff',cursor:isDisabled?'default':'pointer',color:'#212529'}}
        >
          <option value=""></option>
          {normOpts.map((o,i)=><option key={o.key||i} value={o.key}>{o.display}</option>)}
        </select>
        {field.apiKey&&<div style={{fontSize:'10px',color:'#adb5bd',marginTop:'2px'}}>⚡ API key: {field.apiKey}</div>}
      </div>
    );
  }

  if(field.type==='button') {
    const justify = align==='center' ? 'center' : align==='right' ? 'flex-end' : 'flex-start';
    const btnBg = field.bgColor || BRAND;
    const btnText = field.textColor || '#fff';
    const btnBorder = field.borderColor ? `1px solid ${field.borderColor}` : 'none';
    return (
      <div className="action-btn-wrap" style={{display:'flex',justifyContent:justify,width:'100%'}}>
        <button
          type="button"
          disabled={readOnly}
          onClick={()=>onChange({ actionKey: field.actionKey||'custom', triggeredAt: Date.now() })}
          style={{
            height:'34px', padding:'0 16px', width:'auto', minWidth:'120px', maxWidth:'100%',
            background: btnBg, color: btnText, border: btnBorder, borderRadius:'5px',
            fontSize:'12px', fontWeight:600, cursor: readOnly?'not-allowed':'pointer',
            opacity: readOnly?0.6:1, textAlign:'center', display:'flex', alignItems:'center',
            justifyContent:'center', gap:'6px',
          }}
        >
          {field.label||'Action'}
        </button>
      </div>
    );
  }

  // diagnosis_notes / procedure_notes — auto-managed, paired with an Action Button.
  // value is an array of {Code, Name} populated by the host page after the user
  // picks codes in the Diagnosis/Procedure popup. Always visible now — even with
  // no codes yet — sized to match the button's own height (34px) by default, so
  // it's obvious this box exists and is meant to sit next to its button, rather
  // than only appearing once codes have already been picked.
  if(field.type==='diagnosis_notes' || field.type==='procedure_notes') {
    const codes = Array.isArray(value) ? value : [];
    const label = field.label||(field.type==='diagnosis_notes'?'Diagnosis Codes':'Procedure Codes');
    if (codes.length === 0) {
      return (
        <div className="code-notes" style={{height:'34px',display:'flex',alignItems:'center',border:'1px dashed #dee2e6',borderRadius:'6px',padding:'0 14px',background:'#f8f9fa',boxSizing:'border-box'}}>
          <span style={{fontSize:'11px',fontWeight:700,color:'#adb5bd',textTransform:'uppercase',letterSpacing:'.4px'}}>{label}</span>
          <span style={{fontSize:'11px',color:'#adb5bd',marginLeft:'8px'}}>— none selected yet</span>
        </div>
      );
    }
    return (
      <div className="code-notes" style={{border:'1px solid #e9ecef',borderRadius:'6px',padding:'10px 14px',background:'#f8f9fa'}}>
        <div style={{fontSize:'11px',fontWeight:700,color:'#495057',textTransform:'uppercase',letterSpacing:'.4px',marginBottom:'6px'}}>{label}</div>
        <div style={{display:'flex',flexDirection:'column',gap:'3px'}}>
          {codes.map((c,i)=>(
            <div key={c.Code||i} style={{fontSize:'12px',color:'#212529'}}>
              <span style={{fontWeight:700}}>{c.Code}</span>{c.Name?<span style={{color:'#495057'}}> — {c.Name}</span>:null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if(field.type==='multiselect_dropdown') {
  const sel = Array.isArray(value) ? value : [];
  // If prefillKey points to a list in prefillData, use that as options source
  // (e.g. procedures_list: [{key, value}] — extract display strings)
  const rawOpts = (field.prefilled && field.prefillKey && Array.isArray(prefillData[field.prefillKey]))
    ? prefillData[field.prefillKey]
    : (field.options || []);
  const opts = rawOpts.map(o => typeof o === 'string' ? o : (o.value || o.key || ''));
  return wrapField(
    <div className="pdf-hide-trigger">
      <SearchDropdown options={opts} value={sel} multi={true} onChange={v=>onChange(v)} align={align}/>
      {sel.length > 0 && (
        <div style={{fontSize:'11px',color:'#495057',marginTop:'4px',lineHeight:1.6}}>
          {sel.map((s,i) => <div key={i} style={{paddingLeft:'4px'}}>• {s}</div>)}
        </div>
      )}
    </div>
  );
}

  if(field.type==='body_map') return(
    <div>
      {field.label&&<label style={{fontSize:'12px',fontWeight:600,color:'#212529',marginBottom:'6px',display:'block',textAlign:align}}>{field.label}{field.required&&<span style={{color:'#dc3545'}}> *</span>}</label>}
      <BodyMapField field={field} value={value} onChange={onChange} readOnly={readOnly}/>
    </div>
  );

  if(field.type==='dropdown_searchable') {
    const rawOpts = (field.prefilled && field.prefillKey && Array.isArray(prefillData[field.prefillKey]))
      ? prefillData[field.prefillKey]
      : (field.options || []);
    const opts = rawOpts.map(o => typeof o === 'string' ? o : (o.value || o.key || ''));

    // Read-only for doctor role when field uses provider_list as its data source
    if (providerDropdownReadOnly && field.prefillKey === 'provider_list') {
      return wrapField(
        <div style={{...inp, display:'flex', alignItems:'center', justifyContent:align==='center'?'center':align==='right'?'flex-end':'flex-start', background:'#f8f9fa', color:'#495057', cursor:'default', userSelect:'none'}}>
          {value || ''}
        </div>
      );
    }

    return wrapField(
      <SearchDropdown options={opts} value={value||''} onChange={onChange} align={align}/>
    );
  }

  // ── Chip (pill) rendering shared by radio/checkbox when field.displayStyle
  // === 'chips' — an opt-in alternative to the standard checkbox/radio input,
  // toggled per-field in the Properties Panel. Selected state is conveyed by
  // background/border color + aria-pressed, matching the reference sample's
  // clean pill-button look. Kept as an explicit opt-in (default remains the
  // standard input) so no existing form's appearance changes unless the form
  // author turns it on. ──
  const chipJustify = align==='center'?'center':align==='right'?'flex-end':'flex-start';
  const chipBtnStyle = (selected) => ({
    border:`1px solid ${selected?BRAND:'#dee2e6'}`,
    background:selected?BRAND:'#fff',
    color:selected?'#fff':'#212529',
    borderRadius:'999px',
    padding:'5px 14px',
    fontFamily:'inherit',
    fontSize:field.optionFontSize?`${field.optionFontSize}px`:'13px',
    fontWeight:selected?600:400,
    cursor:'pointer',
    outline:'none',
    transition:'border-color .12s, background .12s',
  });

  if(field.type==='radio') {
    if (field.displayStyle==='chips') return wrapField(
      <div style={{display:'flex',gap:'7px',flexWrap:'wrap',justifyContent:chipJustify}}>
        {(field.options||[]).map((o,i)=>{const selected=value===o;return(
          <button key={i} type="button" aria-pressed={selected} onClick={()=>onChange(selected?'':o)} style={chipBtnStyle(selected)}>{o}</button>
        );})}
      </div>
    );
    return wrapField(
      <div style={{display:'flex',gap:'14px',flexWrap:'wrap',justifyContent:chipJustify}}>
        {(field.options||[]).map((o,i)=><label key={i} style={{display:'flex',alignItems:'center',gap:'7px',fontSize:field.optionFontSize?`${field.optionFontSize}px`:'13px',cursor:'pointer'}}><input type="radio" name={field.id} checked={value===o} onChange={()=>{}} onClick={()=>onChange(value===o?'':o)} style={{accentColor:BRAND,width:'15px',height:'15px'}}/>{o}</label>)}
      </div>
    );
  }

  if(field.type==='checkbox') {
    if (field.displayStyle==='chips') return wrapField(
      <div style={{display:'flex',gap:'7px',flexWrap:'wrap',justifyContent:chipJustify}}>
        {(field.options||[]).map((o,i)=>{const selected=(value||[]).includes(o);return(
          <button key={i} type="button" aria-pressed={selected} onClick={()=>{const curr=value||[];onChange(selected?curr.filter(x=>x!==o):[...curr,o]);}} style={chipBtnStyle(selected)}>{o}</button>
        );})}
      </div>
    );
    return wrapField(
      <div style={{display:'flex',gap:'14px',flexWrap:'wrap',justifyContent:chipJustify}}>
        {(field.options||[]).map((o,i)=>{const checked=(value||[]).includes(o);return<label key={i} style={{display:'flex',alignItems:'center',gap:'7px',fontSize:field.optionFontSize?`${field.optionFontSize}px`:'13px',cursor:'pointer'}}><input type="checkbox" checked={checked} onChange={()=>{const curr=value||[];onChange(checked?curr.filter(x=>x!==o):[...curr,o]);}} style={{accentColor:BRAND,width:'15px',height:'15px'}}/>{o}</label>;})}
      </div>
    );
  }

  if(field.type==='date') {
    const toISODate = (v) => {
      if (!v || typeof v !== 'string') return v;
      const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      return m ? `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}` : v;
    };
    // The actual response (value) wins over the auto-filled default
    // (prefillVal) whenever one exists — otherwise a user-entered date would
    // get overwritten back to the prefilled one on the very next render.
    // Auto-filled is just a starting value here too, not a lock (see the
    // readOnly note below).
    const dateVal = toISODate(value||prefillVal||'');
    // Auto-filled is a starting value, not a lock — only the form's overall
    // readOnly state (e.g. viewing an already-finalized note) locks this.
    const dateReadOnly = !!readOnly || lockPre;
    return wrapField(
      <UsDateInput value={dateVal} onChange={onChange} readOnly={dateReadOnly} disabled={dateReadOnly} style={{...inp,textAlign:align,background:dateReadOnly?'#f8f9fa':'#fff',color:dateReadOnly?'#495057':'#212529'}}/>
    );
  }

  // ── Table field ────────────────────────────────────────────
  if(field.type==='table') {
    const cols = field.columns || [];
    const totalW = cols.reduce((s,c)=>s+(c.widthPct||0),0)||100;

    // ── Dynamic mode: when this table is prefilled and the prefill key holds
    // an array, rows are generated from that array (one per item) instead of
    // the manually-configured static rows below — available on any regular Table field. ──
    const dynamicRows = (field.prefilled && field.prefillKey && Array.isArray(prefillData[field.prefillKey]))
      ? prefillData[field.prefillKey] : null;
    if (dynamicRows) {
      const getContrastTextD = (hex) => {
        if (!hex || hex === 'transparent') return '#212529';
        const h = hex.replace('#','');
        if (h.length !== 6) return '#fff';
        const r = parseInt(h.substr(0,2),16), g = parseInt(h.substr(2,2),16), b = parseInt(h.substr(4,2),16);
        const luminance = (0.299*r + 0.587*g + 0.114*b) / 255;
        return luminance > 0.6 ? '#212529' : '#fff';
      };
      const thBgD = field.headerBgColor || BRAND;
      const thStyleD = {padding:'5px 7px',fontSize:'11px',fontWeight:700,color:getContrastTextD(thBgD),background:thBgD,border:'1px solid rgba(255,255,255,0.2)',textAlign:'center',verticalAlign:'middle'};
      const tdStyleD = field.borderless
        ? {padding:'4px 6px',border:'none',verticalAlign:'middle',fontSize:'11px'}
        : {padding:'4px 6px',border:'1px solid #dee2e6',verticalAlign:'middle',fontSize:'11px'};
      const tblAlignD = field.tableAlign || 'left';
      const wrapJustifyD = tblAlignD==='center' ? 'center' : tblAlignD==='right' ? 'flex-end' : 'flex-start';
      // A many-column data table capped at a narrow declared width (half/
      // third/etc.) genuinely doesn't fit its own content on any screen size
      // — the declared width only makes sense for tables with few columns.
      // Past that, just use the available space regardless of what's saved.
      const tblWidthD = cols.length > 4 ? '100%' : getW(field, mobile);
      return wrapField(
        <div style={{display:'flex',justifyContent:wrapJustifyD,width:'100%'}}>
          <div style={{overflowX:'auto',width:tblWidthD}}>
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:`${Math.max(300,cols.length*70)}px`,tableLayout:'fixed'}}>
              {!field.borderless&&(
                <thead>
                  <tr>
                    {cols.map(col=>(
                      <th key={col.id} style={{...thStyleD,width:`${(col.widthPct/totalW*100).toFixed(1)}%`}}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {dynamicRows.length===0 ? (
                  <tr><td colSpan={cols.length||1} style={{...tdStyleD,textAlign:'center',color:'#adb5bd',fontStyle:'italic'}}>No items</td></tr>
                ) : dynamicRows.map((row,ri)=>(
                  <tr key={row?.id??ri}>
                    {cols.map(col=>{
                      const dk = col.dataKey || col.id;
                      const cellVal = row?.[dk];
                      const cellText = cellVal!=null && cellVal!=='' ? String(cellVal) : '';
                      return <td key={col.id} style={{...tdStyleD,width:`${(col.widthPct/totalW*100).toFixed(1)}%`}}>{cellText}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    const tblVal = (typeof value === 'object' && !Array.isArray(value)) ? value : {};
    const rows = field.rows    || [];

    // Normalise cell to always return an array of items (backward-compat with old single-item shape)
    const normCell = (cell) => {
      if (!cell) return [{cellType:'text',options:[]}];
      if (Array.isArray(cell.items)) return cell.items;
      return [{cellType:cell.cellType||'text',options:cell.options||[]}];
    };

    const renderCellItem = (item, cellKey, itemIdx, totalItems) => {
      const itemKey = totalItems > 1 ? `${cellKey}_${itemIdx}` : cellKey;
      const cellVal = tblVal[itemKey];
      const upd = (v) => onChange({...tblVal, [itemKey]: v});
      const inp = {fontSize:'11px',padding:'2px 5px',border:'1px solid #e9ecef',borderRadius:'3px',outline:'none',width:'100%',background:readOnly?'#f8f9fa':'#fff',boxSizing:'border-box',color:'#212529',WebkitTextFillColor:'#212529',colorScheme:'light'};
      if(item.cellType==='label')    return <span style={{fontSize:'11px',fontWeight:600,color:'#212529',background:item.bgColor&&item.bgColor!=='transparent'?item.bgColor:'transparent',padding:item.bgColor&&item.bgColor!=='transparent'?'2px 5px':0,borderRadius:item.bgColor&&item.bgColor!=='transparent'?'3px':0,display:'inline-block'}}>{item.labelText||''}</span>;
      if(item.cellType==='text')     return <input type="text"   style={{...inp,height:'26px'}} value={cellVal||''} onChange={e=>upd(e.target.value)} readOnly={readOnly}/>;
      if(item.cellType==='number')   return <input type="number" style={{...inp,height:'26px',textAlign:'center'}} value={cellVal||''} max={item.max} onChange={e=>{let v=e.target.value; if(item.max!=null&&v!==''&&!isNaN(parseFloat(v))&&parseFloat(v)>item.max)v=String(item.max); upd(v);}} readOnly={readOnly}/>;
      if(item.cellType==='dropdown') return (
        <select style={{...inp,height:'26px',cursor:readOnly?'default':'pointer'}} value={cellVal||''} onChange={e=>upd(e.target.value)} disabled={readOnly}>
          <option value=""></option>
          {(item.options||[]).map((o,i)=><option key={i} value={o}>{o}</option>)}
        </select>
      );
      // ── Chip (pill) rendering for table-cell radio/checkbox items —
      // mirrors the top-level field.displayStyle==='chips' opt-in (see
      // chipBtnStyle above) but at table-cell density: smaller padding/
      // font than the full-size chip button, since a cell is usually only
      // a fraction of the row's width and doesn't have room for the
      // standard 5px/14px pill. Same opt-in pattern — plain radio/checkbox
      // inputs (below) stay the default, so no existing table's appearance
      // changes unless a cell item explicitly sets displayStyle:'chips'. ──
      const cellChipStyle = (selected) => ({
        border:`1px solid ${selected?BRAND:'#dee2e6'}`,
        background:selected?BRAND:'#fff',
        color:selected?'#fff':'#212529',
        borderRadius:'999px',
        padding:'2px 9px',
        fontFamily:'inherit',
        fontSize:'10.5px',
        fontWeight:selected?600:400,
        cursor:readOnly?'default':'pointer',
        outline:'none',
        whiteSpace:'nowrap',
      });
      if(item.cellType==='radio') {
        if (item.displayStyle==='chips') return (
          <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
            {(item.options||[]).map((o,i)=>{const selected=cellVal===o; return(
              <button key={i} type="button" aria-pressed={selected} disabled={readOnly}
                onClick={()=>!readOnly&&upd(selected?'':o)} style={cellChipStyle(selected)}>{o}</button>
            );})}
          </div>
        );
        return (
          <div style={{display:'flex',flexWrap:'wrap',gap:'4px 8px'}}>
            {(item.options||[]).map((o,i)=>(
              <label key={i} style={{display:'flex',alignItems:'center',gap:'3px',fontSize:'11px',cursor:readOnly?'default':'pointer',whiteSpace:'nowrap'}}>
                <input type="radio" checked={cellVal===o} onChange={()=>{}} onClick={()=>!readOnly&&upd(cellVal===o?'':o)} style={{accentColor:BRAND}} readOnly={readOnly}/>
                {o}
              </label>
            ))}
          </div>
        );
      }
      if(item.cellType==='checkbox') {
        const sel=Array.isArray(cellVal)?cellVal:[];
        if (item.displayStyle==='chips') return (
          <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
            {(item.options||[]).map((o,i)=>{const selected=sel.includes(o); return(
              <button key={i} type="button" aria-pressed={selected} disabled={readOnly}
                onClick={()=>!readOnly&&upd(selected?sel.filter(x=>x!==o):[...sel,o])} style={cellChipStyle(selected)}>{o}</button>
            );})}
          </div>
        );
        return (
          <div style={{display:'flex',flexWrap:'wrap',gap:'4px 8px'}}>
            {(item.options||[]).map((o,i)=>(
              <label key={i} style={{display:'flex',alignItems:'center',gap:'3px',fontSize:'11px',cursor:readOnly?'default':'pointer',whiteSpace:'nowrap'}}>
                <input type="checkbox" checked={sel.includes(o)} onChange={()=>!readOnly&&upd(sel.includes(o)?sel.filter(x=>x!==o):[...sel,o])} style={{accentColor:BRAND}}/>
                {o}
              </label>
            ))}
          </div>
        );
      }
      return null;
    };

    // Header text needs to switch between white/dark depending on how light
    // the chosen background is — the palette includes both solid brand green
    // and light pastel presets, and a fixed white text color would be
    // unreadable against the light ones.
    const getContrastText = (hex) => {
      if (!hex || hex === 'transparent') return '#212529';
      const h = hex.replace('#','');
      if (h.length !== 6) return '#fff';
      const r = parseInt(h.substr(0,2),16), g = parseInt(h.substr(2,2),16), b = parseInt(h.substr(4,2),16);
      const luminance = (0.299*r + 0.587*g + 0.114*b) / 255;
      return luminance > 0.6 ? '#212529' : '#fff';
    };
    const thBg = field.headerBgColor || BRAND;
    const thStyle = {padding:'5px 7px',fontSize:'11px',fontWeight:700,color:getContrastText(thBg),background:thBg,border:'1px solid rgba(255,255,255,0.2)',textAlign:'center',verticalAlign:'middle'};
    const tdStyle = field.borderless
      ? {padding:'4px 6px',border:'none',verticalAlign:'middle',fontSize:'11px'}
      : {padding:'4px 6px',border:'1px solid #dee2e6',verticalAlign:'middle',fontSize:'11px'};
    const secStyle = {padding:'5px 8px',background:'#e8f4f1',fontWeight:700,fontSize:'11px',color:'#212529',border:'1px solid #dee2e6',letterSpacing:'0.5px',textTransform:'uppercase'};

    // Table alignment: left (default), center, right
    const tblAlign = field.tableAlign || 'left';
    const wrapJustify = tblAlign==='center' ? 'center' : tblAlign==='right' ? 'flex-end' : 'flex-start';
    // tblWidth uses the standard width field (set by Width buttons in properties)
    // — except a many-column table ignores a narrow declared width entirely,
    // since it genuinely doesn't fit its own content regardless of screen size.
    const tblWidth = cols.length > 4 ? '100%' : getW(field, mobile);

    return wrapField(
      <div style={{display:'flex',justifyContent:wrapJustify,width:'100%'}}>
        <div style={{overflowX:'auto',width:tblWidth}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:`${Math.max(300,cols.length*70)}px`,tableLayout:'fixed'}}>
          {!field.borderless&&(
          <thead>
            <tr>
              {cols.map(col=>(
                <th key={col.id} style={{...thStyle,width:`${(col.widthPct/totalW*100).toFixed(1)}%`}}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          )}
          <tbody>
            {rows.map(row=>{
              if(row.rowType==='section_header') {
                return (
                  <tr key={row.id}>
                    <td colSpan={cols.length} style={field.borderless?{...secStyle,border:'none',background:'transparent'}:secStyle}>{row.label}</td>
                  </tr>
                );
              }
              return (
                <tr key={row.id}>
                  {cols.map(col=>{
                    const cellKey = `${row.id}_${col.id}`;
                    const cell = row.cells?.[col.id];
                    const items = normCell(cell);
                    const colW = `${(col.widthPct/totalW*100).toFixed(1)}%`;
                    // backward-compat: single label item with no custom text shows row label
                    if(items.length===1 && items[0].cellType==='label' && !items[0].labelText) {
                      return <td key={col.id} style={{...tdStyle,width:colW,fontWeight:600,background:field.borderless?'transparent':'#f8f9fa'}}>{row.label}</td>;
                    }
                    // Normalise each item's widthPct against the SUM of all items'
                    // widthPct in this cell — mirrors the col.widthPct/totalW pattern
                    // above. Without this, a cell whose items' widthPct don't add up to
                    // 100 (e.g. checkbox 30 + text 20 + text 20 = 70, a very common
                    // authoring mistake — that's exactly what this table's own schema
                    // has) silently leaves the missing percentage as dead blank space at
                    // the right edge of the cell, before the next column starts. Verified
                    // against a real exported PDF: the unclaimed 30% here matched the
                    // ~30% gap measured between exercise-grid columns almost exactly.
                    const itemsTotalW = items.reduce((s,it)=>s+(it.widthPct||0),0) || 100;
                    // Target 90% of the cell's width, deliberately NOT 100%. A first
                    // attempt normalized items to sum to exactly 100% (with a calc()-based
                    // px deduction to account for the flex container's gap) and it passed
                    // every synthetic reproduction (Playwright, and a direct html2canvas
                    // render of the real schema values) — but a real user-generated PDF
                    // still wrapped the last item onto a second line. Temporary diagnostic
                    // logging shipped to and run by the user (real browser, real DOM,
                    // right before html2canvas captures) showed why: even with this
                    // container's `gap` confirmed at computed 0px (the existing
                    // `.frm-content [style*="gap"]` override), real Chrome layout still
                    // left ~6px of unexplained space at each item boundary — e.g. a
                    // 3-item row in a 245.6px cell needed roughly 12-13px more than the
                    // sum of the items' own declared widths before the last item stopped
                    // wrapping. Rather than chase that exact number with another
                    // calc()-based deduction (which could just as easily be invalidated
                    // by a different font, zoom level, or browser), this leaves a large,
                    // plain-percentage safety margin instead: filling 90% of the cell
                    // (versus the original, buggy 70%) still nearly triples the usable
                    // width per item, while leaving a real ~10% cushion — comfortably
                    // more than double the ~5% overhead actually measured.
                    const TARGET_TOTAL_PCT = 90;
                    return (
                      <td key={col.id} style={{...tdStyle,width:colW}}>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'4px',alignItems:'flex-start'}}>
                          {items.map((item,itemIdx)=>{
                            const itemPct = items.length>1
                              ? (((item.widthPct||(itemsTotalW/items.length))/itemsTotalW)*TARGET_TOTAL_PCT).toFixed(1)
                              : 100;
                            const itemW = items.length>1 ? `${itemPct}%` : '100%';
                            return (
                              <div key={item.id||itemIdx} style={{width:itemW,minWidth:'40px',boxSizing:'border-box'}}>
                                {renderCellItem(item, cellKey, itemIdx, items.length)}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    );
  }
  if(field.type==='file_upload') {
    return wrapField(<FileUploadField field={field} value={value} onChange={onChange} readOnly={readOnly}/>);
  }
  if(field.type==='referral_attachments') return null; // staff-only (Medical Notes)
  if(field.type==='patient_info_strip') {
    const items = (field.items||[]).filter(it=>it.visible!==false);
    const stripBg = field.bgColor || BRAND_LIGHT;

    const providerNameRow    = field.providerNameRow    || {};
    const providerAddressRow = field.providerAddressRow || {};
    const providerNameVal    = providerNameRow.prefillKey    ? prefillData[providerNameRow.prefillKey]    : undefined;
    const providerAddressVal = providerAddressRow.prefillKey ? prefillData[providerAddressRow.prefillKey] : undefined;

    return (
      <div style={{display:'flex',flexDirection:'column',gap:'6px',width:'100%'}}>
        {providerNameRow.visible!==false && (
          <div style={{textAlign:'center',fontWeight:700,fontSize:'13px',color:'#212529',padding:'6px 10px',background:providerNameRow.bgColor||'transparent',borderRadius:'4px',boxSizing:'border-box'}}>
            {(providerNameVal!=null && providerNameVal!=='') ? providerNameVal : '—'}
          </div>
        )}
        {providerAddressRow.visible!==false && (
          <div style={{textAlign:'center',fontWeight:400,fontSize:'12px',color:'#495057',padding:'6px 10px',background:providerAddressRow.bgColor||'transparent',borderRadius:'4px',boxSizing:'border-box'}}>
            {(providerAddressVal!=null && providerAddressVal!=='') ? providerAddressVal : '—'}
          </div>
        )}
        {field.showFormName!==false && (
          <div style={{textAlign:'center',fontWeight:700,fontSize:'14px',color:'#212529',padding:'4px 10px',boxSizing:'border-box'}}>
            {formName}
          </div>
        )}
        <div style={{display:'flex',flexWrap:'wrap',justifyContent:'center',alignItems:'center',gap:'18px',padding:'8px 14px',background:stripBg,border:`1px solid ${BRAND}`,borderRadius:'6px',boxSizing:'border-box',width:'100%'}}>
          {items.map(it=>{
            const val = it.prefillKey ? prefillData[it.prefillKey] : undefined;
            return (
              <span key={it.id} style={{fontSize:'12px',whiteSpace:'nowrap'}}>
                <span style={{fontWeight:700,color:'#495057'}}>{it.label}:</span>{' '}
                <span style={{color:'#212529'}}>{(val!=null && val!=='') ? val : '—'}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  }
  return wrapField(
    <input
      type={field.type==='email'?'email':field.type==='number'?'number':field.type==='phone'?'tel':'text'}
      value={fmtUSDate(value!==null&&value!==undefined&&value!==''?value:(prefillVal!==null&&prefillVal!==undefined?prefillVal:''))}
      onChange={e=>{
        let v = e.target.value;
        if (field.type==='number' && field.max!=null && v!=='' && !isNaN(parseFloat(v)) && parseFloat(v) > field.max) v = String(field.max);
        onChange(v);
      }}
      placeholder={field.placeholder||''}
      // Auto-filled is a starting value, not a lock — the person filling the
      // form must still be able to correct/edit it (e.g. a wrong prefilled
      // name). Only the form's overall readOnly state (e.g. viewing an
      // already-finalized note) locks this.
      readOnly={!!readOnly || lockPre}
      max={field.type==='number'?field.max:undefined}
      style={{...inp,textAlign:align,background:(readOnly||lockPre)?'#f8f9fa':'#fff',color:(readOnly||lockPre)?'#495057':'#212529'}}
    />
  );
}

// ── Validation ────────────────────────────────────────────────────────────────
function validate(fields, responses) {
  const errors={};
  fields.forEach(f=>{
    if(!f.required||isLayout(f.type)||f.type==='inline_row') return;
    const v=responses[f.id];
    if(isSig(f.type)){ if(!v) errors[f.id]='Signature is required'; return; }
    if(f.type==='checkbox'){ if(!v||!v.length) errors[f.id]='Please select at least one option'; return; }
    if(f.type==='referral_attachments'){ if(!v||!v.length) errors[f.id]='Please attach at least one referral'; return; }
    if(f.type==='single_checkbox'){ if(!v) errors[f.id]='This field is required'; return; }
    if(!v||v===''||v===null||v===undefined) errors[f.id]=`${f.label||'This field'} is required`;
  });
  return errors;
}

export default function FormRenderer({
  schema,
  prefillData = {},
  onSubmit,
  onChange,
  onBack,
  backLabel = '← Back to List',
  readOnly = false,
  initialValues = {},
  mobile = false,
  hideSaveButtons = false,
  hideTopbar = false,
  hidePreviewToggle = false,
  valueOverrides = {},
  onFieldChange,
  onSignatureClick = null,
  suppressedFieldTypes = [], // field types to render as null (e.g. sig_supervisor when ImplicitDoctorSignatureOnNote is false)
  providerDropdownReadOnly = false,
  disabledPrefilled = false, // patient app: auto-filled fields are read-only
  suppressedFieldIds = [],   // field ids to hide (hideInPatientApp)
}) {

  const [errors,     setErrors]     = useState({});
  const toggleSection = (key, currentlyCollapsed) => setCollapsedSections(prev => ({...prev, [key]: !currentlyCollapsed}));
  const [previewMode,setPreviewMode]= useState(mobile?'mobile':'desktop');
  // Sync when mobile prop changes (e.g. editor toggles Desktop/Mobile)
  useEffect(()=>{ setPreviewMode(mobile?'mobile':'desktop'); },[mobile]);

  // API: schemaJson is the JSON string (normalized from schemaJsonPath by fetchTemplateById)
  // Local builder: passes fields directly on schema.fields
  const parsedSchema = (() => {
    if (schema?.schemaJson) {
      try { return JSON.parse(schema.schemaJson); } catch { return {}; }
    }
    return schema || {};
  })();
  const fields = parsedSchema.fields || schema?.fields || [];
  const formName = schema?.formTemplateName || schema?.name || 'Form';
  const [responses, setResponses] = useState(() => {
    // Start with saved data (from GetFormTemplateByCaseId)
    const init = {...initialValues};

    if (fields.length) {
      fields.forEach(f => {
        // Prefilled fields: prefillData ALWAYS wins over saved data
        // This ensures live patient data (name, DOB, etc.) always shows current values
        if (f.prefilled && f.prefillKey && prefillData[f.prefillKey]) {
          const prefillVal = prefillData[f.prefillKey];
          // Only scalar values — arrays/objects are dropdown data sources, not field values
          if (typeof prefillVal !== 'object') {
            // Date-type fields keep ISO (their input converts for display); any
            // other field (e.g. a text box prefilled with the accident date)
            // gets MM/dd/yyyy so that's what's shown, printed and saved.
            init[f.id] = f.type === 'date' ? prefillVal : fmtUSDate(prefillVal);
          }
        }
        // Doctor Signature: auto-fill the physician's name by default — including on
        // fields saved before this existed, which have no explicit prefilled/prefillKey
        // at all (undefined, not false). Treat "not explicitly turned off" as on, same as
        // the builder's own checkbox default, and fall back to referring_physician (the
        // field FormBuilderEditor.js's own panel defaults to) when no prefillKey is set.
        // Harmless if the generic rule above already set this field the same way.
        // Requested by Ajay 2026-10-05.
        // Skipped when the field is locked to Draw mode (f.signatureMode==='draw') —
        // SignaturePad's canvas expects value to be '' or a data:image URL (it does
        // `img.src = value`), so forcing a plain name string in there wouldn't
        // render anything and would leave a bogus non-signature value saved.
        if (f.type === 'sig_doctor' && f.prefilled !== false && f.signatureMode !== 'draw') {
          // Different host screens populate the signing physician's name under
          // different prefillData keys — the Referral Form modal uses
          // referring_physician, while MedicalNoteFormBuilder.js's own
          // prefillData only ever sets doctor_name (no referring_physician key
          // exists there at all). Try the field's own explicit prefillKey
          // first (so a deliberately-chosen Data Field always wins), then
          // both known host keys, in order.
          const sigVal = resolveSigDoctorName(f, prefillData);
          if (sigVal !== undefined) init[f.id] = sigVal;
        }
        // provider_dropdown: always use current provider_id from prefill
        if (f.type === 'provider_dropdown' && prefillData['provider_id']) {
          init[f.id] = prefillData['provider_id'];
        }
        // regular dropdown with provider_list: pre-select by provider_id key
        if (f.type === 'dropdown' && f.prefillKey === 'provider_list' && prefillData['provider_id']) {
          init[f.id] = prefillData['provider_id'];
        }
        // dropdown_searchable with provider_list: resolve provider_id key → display name
        if (f.type === 'dropdown_searchable' && f.prefillKey === 'provider_list' && prefillData['provider_id'] && Array.isArray(prefillData['provider_list'])) {
          const match = prefillData['provider_list'].find(o => o.key === prefillData['provider_id']);
          if (match) init[f.id] = match.value;
        }
        // inline_row: prefilled segments always override saved segment values
        if (f.type === 'inline_row' && f.segments) {
          const segVals = { ...(init[f.id] || {}) };
          let changed = false;
          f.segments.forEach(s => {
            // Provider Dropdown segment: always start on the current provider's
            // option text (mirrors the standalone provider_dropdown field).
            if (s.fieldType === 'provider_dropdown') {
              const cur = Array.isArray(prefillData['provider_list'])
                ? prefillData['provider_list'].find(o => String(o.key) === String(prefillData['provider_id']))
                : null;
              if (cur?.value) { segVals[s.id] = cur.value; changed = true; }
              return;
            }
            if (s.prefilled && s.prefillKey && prefillData[s.prefillKey] && typeof prefillData[s.prefillKey] !== 'object') {
              segVals[s.id] = prefillData[s.prefillKey];
              changed = true;
            }
          });
          if (changed) init[f.id] = segVals;
        }
        // Default Value (Text Input) — lowest priority: only seeds a starting
        // value when neither a saved response nor a live prefill already set
        // one above. From here it's just normal editable response state, so
        // the user can change it same as any other field (mirrors how the
        // Rich Text Area's own `content` seed works).
        if (f.type === 'text' && f.defaultValue && init[f.id] === undefined) {
          init[f.id] = f.defaultValue;
        }
        if (f.type === 'inline_row' && f.segments) {
          const segDefaults = {};
          let hasDefaults = false;
          f.segments.forEach(s => {
            if (s.segType === 'field' && s.fieldType === 'text' && s.defaultValue) {
              const existing = (init[f.id] || {})[s.id];
              if (existing === undefined) { segDefaults[s.id] = s.defaultValue; hasDefaults = true; }
            }
          });
          if (hasDefaults) init[f.id] = { ...segDefaults, ...(init[f.id] || {}) };
        }
      });
    }
    return init;
  });

  // sig_doctor auto-fill, part 2: re-apply once prefillData actually resolves.
  // The one-time init block above only ever sees whatever prefillData looked
  // like at the exact instant this component first mounted — fine for a host
  // that already has prefillData ready by then (the Referral Form modal), but
  // MedicalNoteFormBuilder.js builds its prefillData asynchronously, inside
  // its own useEffect, AFTER an API call resolves. So on that screen this
  // component mounts first with an empty prefillData, the one-time init block
  // runs and finds nothing, and prefillData only gets the real doctor_name
  // moments later — too late for a useState lazy initializer to ever see.
  // Confirmed 2026-10-06 via a diagnostic log: both referring_physician and
  // doctor_name read back undefined at mount time on that screen, even though
  // the physician really was assigned — that's this race, not a missing key.
  // This effect re-checks every time prefillData itself changes and applies
  // the resolved name once it's actually available, matching Ajay's explicit
  // choice (2026-10-05) to always refresh to the current physician name
  // rather than only fill once.
  // 2026-10-08: hosts hand in a NEW prefillData object on many renders (even
  // when the physician's name is unchanged), and this effect used to stamp the
  // name over the field every time — wiping a signature the doctor had just
  // drawn (and any name edit) the next time the host re-rendered, e.g. after
  // flipping Draw -> Type -> Draw. Now it only acts when the RESOLVED NAME
  // itself changes (late-arriving prefill, or a different physician), and
  // never replaces a drawn signature image.
  const lastSigNameRef = useRef({});
  useEffect(() => {
    if (!fields.length) return;
    const updates = {};
    let changed = false;
    fields.forEach(f => {
      if (f.type !== 'sig_doctor' || f.prefilled === false || f.signatureMode === 'draw') return;
      const resolved = resolveSigDoctorName(f, prefillData);
      const prevResolved = lastSigNameRef.current[f.id];
      lastSigNameRef.current[f.id] = resolved;
      if (resolved === prevResolved) return;
      if (isDrawnSigValue(responses[f.id])) return;
      if (resolved !== undefined && responses[f.id] !== resolved) {
        updates[f.id] = resolved;
        changed = true;
      }
    });
    if (changed) setResponses(prev => ({ ...prev, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // Per-section expand/collapse state for collapsible `section_start` cards,
  // keyed by the section_start field's id. Absent from this map means "use
  // that field's own defaultCollapsed" (see the render loop below).
  // Collapsing is purely an on-screen fill convenience: PDF/print always
  // forces every section open (see the .frm-section-body PDF override).
  // Demo feedback (2026-09-29): every collapsible section should start
  // collapsed regardless of the schema's own defaultCollapsed — EXCEPT when
  // this render already has real saved answers inside it (opening an
  // existing note in MedicalNoteFormBuilder for editing), in which case it
  // starts expanded so the clinician isn't hiding their own prior work
  // behind a click. Computed once, synchronously, from the `responses` just
  // resolved above (initialValues/prefillData are already merged in by
  // then) — not re-evaluated as the user types during this session, so an
  // accordion doesn't spring open mid-fill.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const initial = {};
    buildSections(buildRows(fields)).forEach(section => {
      if (!section.collapsible) return;
      const hasSavedValue = section.rows.flat().some(f =>
        !isLayout(f.type) && !isFieldEmptyForPrint(f, responses[f.id])
      );
      initial[section.key] = !hasSavedValue;
    });
    return initial;
  });

  // diagnosis_notes / procedure_notes are now normal, visible, editable fields
  // in the builder canvas — positioned and sized there directly — so their
  // order and width here just come from the saved schema like any other
  // field. No render-time reordering or width-forcing needed anymore.
  const orderedFields = fields;
  const rows = buildRows(orderedFields);
  // Tabs partition the whole form (see buildTabs) — computed unconditionally
  // every render so the activeTab hook below stays in a stable position.
  // tabs.length<=1 means the form has no tab_break fields; the render below
  // then skips the tab bar entirely and behaves exactly as it always did.
  const tabs = buildTabs(rows);
  const [activeTab, setActiveTab] = useState(() => tabs[0]?.key || null);
  // A schema edit (e.g. live in the builder's preview pane) can add/remove
  // tabs between renders, which could leave activeTab pointing at a tab that
  // no longer exists — fall back to the first tab rather than showing blank.
  const currentTabKey = tabs.some(t=>t.key===activeTab) ? activeTab : tabs[0]?.key;

  // Keep a ref to fields so the valueOverrides effect always sees the latest schema
  // Initialize synchronously (not just in useEffect) so it's available on first render
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields; // sync update every render
  useEffect(() => { fieldsRef.current = fields; }, [fields]);
  const responsesRef = useRef({});
  useEffect(() => { responsesRef.current = responses; }, [responses]);

  // ── Merge valueOverrides reactively (provider cascade + signature auto-date) ──
  // valueOverrides is a { fieldId: value } map built by the parent from the schema
  // Hosts pass the WHOLE accumulated override map (Intake builds a fresh object
  // every render; Medical Notes spreads prev into a new one on every push), so
  // this effect fires with the old entries too. Re-applying an entry that has
  // not changed since we last applied it silently overwrites whatever the user
  // did afterwards — e.g. Draw -> Type -> Draw on a signature box reverted to
  // the stale loaded image / '' and the drawing "disappeared". So each key is
  // applied only when its override VALUE differs from what we last applied.
  const lastAppliedOverridesRef = useRef({});
  // `_force` sentinel: a host that wants the SAME override value pushed again (e.g. the
  // intake sheet re-copying one signature onto a box the patient has since cleared)
  // changes valueOverrides._force; when that token changes every override is re-applied
  // once, bypassing the unchanged-value check below.
  const lastForceRef = useRef(undefined);
  useEffect(() => {
    if (!valueOverrides || !Object.keys(valueOverrides).length) return;
    const lastApplied = lastAppliedOverridesRef.current;
    const forceToken = valueOverrides._force;
    const forced = forceToken !== undefined && forceToken !== lastForceRef.current;
    if (forced) lastForceRef.current = forceToken;
    const pending = Object.entries(valueOverrides).filter(([key, val]) =>
      key !== '_ts' && key !== '_sigAutoDate' && key !== '_force' &&
      (forced || !(Object.prototype.hasOwnProperty.call(lastApplied, key) && Object.is(lastApplied[key], val)))
    );
    if (!pending.length) return;
    pending.forEach(([key, val]) => { lastApplied[key] = val; });
    const pendingMap = Object.fromEntries(pending);
    setResponses(prev => {
      const updated = { ...prev };
      Object.entries(pendingMap).forEach(([key, val]) => {
        if (key === '_ts' || key === '_sigAutoDate' || key === '_force') return; // ignore sentinel keys
        // Direct fieldId match
        if (updated[key] !== undefined || fieldsRef.current.some(f => f.id === key)) {
          const targetField = fieldsRef.current.find(f => f.id === key);
          if (targetField?.type === 'dropdown_searchable' && targetField?.prefillKey === 'provider_list' && Array.isArray(prefillData['provider_list'])) {
            const match = prefillData['provider_list'].find(o => o.key === val);
            updated[key] = match ? match.value : val;
          } else if (targetField?.type === 'dropdown' && targetField?.prefillKey === 'provider_list') {
            updated[key] = val;
          } else {
            updated[key] = val;
          }

          // ── Auto-fill linked date when a signature is pushed via valueOverrides ──
          // Only fires when the parent sets _sigAutoDate: true (user-triggered sign),
          // not on initial pre-load from backend (where the saved date should be kept).
          if (targetField && isSig(targetField.type) && val && valueOverrides._sigAutoDate === true) {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
            fieldsRef.current.forEach(f => {
              if (f.type === 'date' && f.signedDateFor === key && !updated[f.id]) {
                updated[f.id] = todayStr;
              }
            });
          }
          return;
        }
        // `${fieldId}__${segmentId}` — an override aimed at one segment of an
        // inline row (provider cascade into inline-row segments).
        const sepIdx = key.indexOf('__');
        if (sepIdx > 0) {
          const rowField = fieldsRef.current.find(f => f.id === key.slice(0, sepIdx) && f.type === 'inline_row');
          if (rowField) {
            updated[rowField.id] = { ...(updated[rowField.id] || {}), [key.slice(sepIdx + 2)]: val };
            return;
          }
        }
        // Fallback: treat key as prefillKey and find matching field
        fieldsRef.current.forEach(f => {
          if (f.prefillKey === key) updated[f.id] = val;
          if (f.type === 'inline_row' && f.segments) {
            f.segments.forEach(s => { if (s.prefillKey === key) { updated[f.id] = { ...(updated[f.id]||{}), [s.id]: val }; } });
          }
        });
      });
      return updated;
    });
  }, [valueOverrides]); // eslint-disable-line

  useEffect(() => {
  if (onChange) onChange(responses);
}, [responses]); // eslint-disable-line

  // Use a ref for onFieldChange to avoid stale closure issues in setVal
  const onFieldChangeRef = useRef(onFieldChange);
  useEffect(() => { onFieldChangeRef.current = onFieldChange; }, [onFieldChange]);

  const setVal = useCallback((fieldId, val, field) => {
    const updates = {[fieldId]: val};

    // ── Auto-fill date when signature is drawn ────────────────────────────────
    // Only fills date fields that have signedDateFor === this signature's fieldId
    if (val && field && isSig(field.type)) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
      fieldsRef.current.forEach(f => {
        if (f.type === 'date' && f.signedDateFor === fieldId) {
          updates[f.id] = todayStr;
        }
      });
    }

    setResponses(prev => ({...prev, ...updates}));
    setErrors(prev => {const e={...prev}; delete e[fieldId]; return e;});
    if (onFieldChangeRef.current) onFieldChangeRef.current(fieldId, val, field);
  }, []); // eslint-disable-line

  // ── FIX #4: Preview is full-width, no max-width cap on desktop ─────────────
  const formWidth = previewMode==='mobile' ? 'min(390px, 100%)' : '100%';
  const isMob = previewMode==='mobile';

  return (
    <>
      <style>{`
        .frm-wrap { display:flex;flex-direction:column;min-height:100vh;background:#f4f5f7; }
        .frm-topbar { display:flex;align-items:center;gap:8px;padding:6px 14px;background:#fff;border-bottom:1px solid #dee2e6;flex-shrink:0; }
        .frm-back { background:none;border:1px solid #dee2e6;height:28px;padding:0 10px;border-radius:4px;font-size:12px;color:#6c757d;cursor:pointer; }
        .frm-back:hover { background:#f8f9fa; }
        .frm-body { flex:1;overflow-y:visible; }
        .frm-body.mobile-center { display:flex;justify-content:center; }
        .frm-card { background:#fff;overflow:hidden; }
        .frm-card.desktop { border-radius:0;box-shadow:none; }
        .frm-card.mobile  { border-radius:8px;box-shadow:0 2px 16px rgba(0,0,0,.12);margin:16px; }
        .frm-header { background:${BRAND};padding:10px 20px; }
        .frm-title  { font-size:18px;font-weight:700;color:#fff; }
        .frm-subtitle { font-size:13px;color:rgba(255,255,255,.8);margin-top:3px; }
        .frm-content { padding:10px 20px; background:#f4f5f7; }
        .frm-row { display:flex;flex-wrap:wrap;gap:8px;margin-bottom:5px;align-items:flex-start; }
        .frm-field-error { font-size:11px;color:#dc3545;margin-top:3px; }
        .frm-field-wrap { position:relative; box-sizing:border-box; }
        .frm-para ul,.frm-para ol,[contenteditable] ul,[contenteditable] ol { padding-left:22px; margin:4px 0; }
        .frm-para li,[contenteditable] li { margin:2px 0; }
        .frm-field-wrap.has-error input,.frm-field-wrap.has-error select,.frm-field-wrap.has-error textarea { border-color:#dc3545 !important; };color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer; }
        .frm-spin { width:12px;height:12px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite;display:inline-block;margin-right:6px; }
        @keyframes spin{to{transform:rotate(360deg);}}
        [contenteditable]:focus { outline:none;border-color:${BRAND} !important;box-shadow:0 0 0 2px ${BRAND_LIGHT}; }
        .rta-toolbar { display:flex; }
        [contenteditable] { color:#212529 !important; -webkit-text-fill-color:#212529 !important; color-scheme:light; background-color:#fff; }
        .frm-content input, .frm-content select, .frm-content textarea {
          color:#212529 !important; -webkit-text-fill-color:#212529 !important; color-scheme:light;
        }
        .frm-content input::placeholder, .frm-content textarea::placeholder { color:#adb5bd !important; -webkit-text-fill-color:#adb5bd !important; }
      `}</style>

      <div className="frm-wrap">
        {/* Top bar */}
        {!hideTopbar && (onBack||!hideSaveButtons) && (
          <div className="frm-topbar">
            {onBack&&<button className="frm-back" onClick={onBack}>{backLabel}</button>}
            <span style={{fontSize:'13px',fontWeight:600,color:'#212529',marginLeft:'8px'}}>{schema?.formTemplateName||schema?.name||'Form Preview'}</span>
            <div style={{flex:1}}/>
            {/* Desktop/mobile toggle — hidden when hidePreviewToggle=true */}
            {!hidePreviewToggle && (
              <div style={{display:'flex',border:'1px solid #dee2e6',borderRadius:'5px',overflow:'hidden'}}>
              <button onClick={()=>setPreviewMode('desktop')} style={{height:'27px',padding:'0 12px',fontSize:'11px',fontWeight:600,border:'none',cursor:'pointer',background:previewMode==='desktop'?BRAND:'#fff',color:previewMode==='desktop'?'#fff':'#6c757d'}}>🖥 Desktop</button>
              <button onClick={()=>setPreviewMode('mobile')}  style={{height:'27px',padding:'0 12px',fontSize:'11px',fontWeight:600,border:'none',cursor:'pointer',background:previewMode==='mobile'?BRAND:'#fff',color:previewMode==='mobile'?'#fff':'#6c757d'}}>📱 Mobile</button>
            </div>
            )}
          </div>
        )}

        <div className={`frm-body ${isMob?'mobile-center':''}`}>
          <div style={{width:formWidth}}>
            <div className={`frm-card ${isMob?'mobile':'desktop'}`}>
              {tabs.length>1 && (
                <div className="frm-tab-bar" style={{display:'flex',gap:'4px',padding:'8px 20px 0',borderBottom:'2px solid #e9ecef',overflowX:'auto'}}>
                  {tabs.map((tab,tabIdx)=>{
                    const active = tab.key===currentTabKey;
                    return (
                      <button key={tab.key} type="button" onClick={()=>setActiveTab(tab.key)}
                        style={{
                          display:'flex',alignItems:'center',gap:'7px',
                          border:'none',background:'none',cursor:'pointer',whiteSpace:'nowrap',
                          padding:'8px 14px',fontSize:'13px',fontWeight:600,
                          color: active ? '#212529' : '#6c757d',
                          borderBottom: active ? `3px solid ${BRAND}` : '3px solid transparent',
                          marginBottom:'-2px',
                        }}>
                        {/* Circle badge showing the tab's own first letter (matching
                            the reference tool's .tab .l badge — P/S/O/A/P/C style,
                            not a sequential step number) — gray by default,
                            brand-filled + white text on the active tab. Purely
                            cosmetic; every tab's content still stays mounted in
                            the DOM (see buildTabs) regardless of which one is
                            "active" on screen. */}
                        <span style={{
                          display:'inline-grid',placeItems:'center',width:'22px',height:'22px',
                          borderRadius:'50%',flexShrink:0,fontSize:'11px',fontWeight:700,
                          background: active ? BRAND : '#dee2e6',
                          color: active ? '#fff' : '#495057',
                        }}>{(tab.label||'').trim().charAt(0).toUpperCase()||(tabIdx+1)}</span>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="frm-content">
                {fields.length===0
                  ?<div style={{textAlign:'center',padding:'32px',color:'#adb5bd',fontSize:'14px'}}>No fields in this form.</div>
                  :(()=>{
                    // Build prefillKey→live value map so {{vars}} in paragraphs always show current values
                    // IMPORTANT: skip array values (e.g. provider_list) — those are options sources, not field values
                    // (hoisted out of the per-row loop below — it doesn't depend on `row`, only on `fields`/`responses`)
                    const liveValues = {...prefillData};
                    fields.forEach(f => {
                      if (f.prefillKey && responses[f.id] !== undefined && responses[f.id] !== null && responses[f.id] !== '') {
                        // Only overwrite scalars — never overwrite arrays/objects (e.g. provider_list, procedures_list)
                        if (!Array.isArray(prefillData[f.prefillKey]) && typeof prefillData[f.prefillKey] !== 'object') {
                          liveValues[f.prefillKey] = responses[f.id];
                        }
                      }
                    });
                    const renderRow = (row, key) => (
                      <div key={key} className="frm-row">
                        {row.map(f=>{
                          const w = getW(f,isMob);
                          const overflow = f.type==='inline_row' ? {overflowX:'auto'} : {};
                          const err = errors[f.id];
                          return(
                            <div key={f.id} style={{width:w,...overflow}} className={`frm-field-wrap ${err?'has-error':''}`} data-field-id={f.id} data-field-type={f.type}>
                              <FieldRenderer
                                field={f}
                                value={responses[f.id]}
                                onChange={v=>setVal(f.id,v,f)}
                                prefillData={liveValues}
                                responses={responses}
                                mobile={isMob}
                                readOnly={readOnly}
                                providerDropdownReadOnly={providerDropdownReadOnly}
                                onSignatureClick={onSignatureClick}
                                suppressedFieldTypes={suppressedFieldTypes}
                                suppressedFieldIds={suppressedFieldIds}
                                disabledPrefilled={disabledPrefilled}
                                setVal={setVal}
                                formName={formName}
                              />
                              {err&&<div className="frm-field-error">⚠ {err}</div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                    const renderSections = (sectionRows) => buildSections(sectionRows).map(section=>{
                      let content;
                      if (section.collapsible) {
                        const [headerRow, ...bodyRows] = section.rows;
                        const collapsed = collapsedSections[section.key] !== undefined ? collapsedSections[section.key] : section.defaultCollapsed;
                        // ── Collapsed state: a compact, content-width pill — not the
                        // full-width bordered card — so there's no leftover blank strip
                        // between the title (left) and the +/- icon (right edge of a
                        // full-width row with nothing in between). A colored full-width
                        // bar was tried first but that just repaints the empty space
                        // instead of removing it; shrinking the whole header to hug its
                        // own content is what actually gets rid of it. The section's
                        // fields still render into the DOM (just display:none) — never
                        // skipped — so PDF export's "force every section open for print"
                        // rule (see the .frm-section-body override in generatePdfHtml)
                        // still has real content to reveal even if the form was left
                        // collapsed when the user hit Save & Print. ──
                        if (collapsed) {
                          const startField = headerRow.find(f=>f.type==='section_start');
                          const pill = (
                            <Fragment>
                              <div className="frm-section-toggle frm-section-toggle-collapsed" onClick={()=>toggleSection(section.key,collapsed)}
                                style={{cursor:'pointer',display:'inline-flex',alignItems:'center',gap:'10px',padding:'7px 12px 7px 14px',borderRadius:'8px',background:'#f1f3f5',border:'1px solid #e9ecef',marginBottom:'10px'}}>
                                <span style={{fontSize:'15px',fontWeight:700,color:'#212529',lineHeight:1.3,whiteSpace:'nowrap'}}>{startField?.label||''}</span>
                                <span className="frm-section-toggle-icon" style={{flexShrink:0,width:'20px',height:'20px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',fontWeight:700,lineHeight:1,color:'#495057',background:'#fff',border:'1px solid #ced4da',borderRadius:'4px'}}>+</span>
                              </div>
                              <div className="frm-section-body" style={{display:'none'}}>
                                {bodyRows.map((row,i)=>renderRow(row,`${section.key}_${i+1}`))}
                              </div>
                            </Fragment>
                          );
                          // Still tagged with the `.frm-section-card` class (styled here to
                          // be visually invisible/content-hugging on screen — no border,
                          // background, or padding, so this changes nothing about the
                          // compact-pill look above) purely so PDF export's "only print
                          // filled fields" pass (see printFilledOnly/emptySectionCards
                          // below) can find this collapsed accordion's title+body as one
                          // unit and hide the whole thing — title included — when every
                          // answer field inside is empty. Without this marker, a section
                          // left collapsed with nothing filled in printed a bare heading
                          // with nothing under it (reported 2026-09-28): the title lived
                          // outside any `.frm-section-card`, so the "hide the whole empty
                          // card" logic had no card to find and hide it with.
                          return section.cardWrap
                            ? <div key={section.key} className="frm-section-card" style={{background:'transparent',border:'none',borderRadius:0,boxShadow:'none',padding:0,margin:0,display:'inline-block'}}>{pill}</div>
                            : <Fragment key={section.key}>{pill}</Fragment>;
                        }
                        content = (
                          <Fragment>
                            <div className="frm-section-toggle" onClick={()=>toggleSection(section.key,collapsed)}
                              style={{cursor:'pointer',display:'flex',alignItems:'center',gap:'8px'}}>
                              <div style={{flex:1,minWidth:0}}>{renderRow(headerRow,`${section.key}_0`)}</div>
                              {/* Explicit +/- indicator (was a bare ▾ chevron floating to the left of
                                  the title, easy to miss — especially against a colored title bar).
                                  A bordered square with a high-contrast +/- glyph reads as clickable
                                  regardless of the title's own background color, and sits at the
                                  right edge, out of the title text's way. Purely visual — no onClick
                                  of its own — the whole row above already toggles on click, so a
                                  separate handler here would just double-fire and cancel itself out. */}
                              <span className="frm-section-toggle-icon" style={{flexShrink:0,width:'20px',height:'20px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',fontWeight:700,lineHeight:1,color:'#495057',background:'#fff',border:'1px solid #ced4da',borderRadius:'4px'}}>−</span>
                            </div>
                            <div className="frm-section-body">
                              {bodyRows.map((row,i)=>renderRow(row,`${section.key}_${i+1}`))}
                            </div>
                          </Fragment>
                        );
                      } else {
                        content = section.rows.map((row,i)=>renderRow(row,`${section.key}_${i}`));
                      }
                      return section.cardWrap
                        ? <div key={section.key} className="frm-section-card" style={SECTION_CARD_STYLE}>{content}</div>
                        : <Fragment key={section.key}>{content}</Fragment>;
                    });
                    // Tabs are a pure on-screen navigation affordance (see buildTabs) —
                    // every tab's content is ALWAYS rendered into the DOM; only the
                    // inactive ones are display:none'd on screen, so PDF capture (which
                    // reads this same live DOM) still finds and prints every tab's
                    // fields, forced visible by the .frm-tab-panel PDF override below.
                    return tabs.length>1
                      ? tabs.map(tab=><div key={tab.key} className="frm-tab-panel" style={{display: tab.key===currentTabKey ? undefined : 'none'}}>
                          {renderSections(tab.rows)}
                        </div>)
                      : renderSections(rows);
                  })()
                }
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}